"use client";

import React, { useState, useEffect, useMemo, useRef } from "react";
import NextImage, { type ImageProps } from "next/image";
import { getPropertyHref } from "@/lib/property-url";
import { Button } from "@/components/ui/button";
import {
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  ChevronRight,
  X,
  Plus,
  Minus,
  Camera,
  Heart,
  Share2,
  Bookmark,
  Bed,
  Bath,
  Square,
  Car,
  Shield,
  MapPin,
  Flag,
  Star,
  Check,
  Calendar as CalendarIcon,
  ImageOff,
  Copy,
  Mail,
  CheckCircle2,
  Upload,
  Loader2,
  FileUp,
} from "lucide-react";
import Link from "next/link";
import { getAmenityIcon } from "@/lib/amenity-icons";
import { EnquiryDialog } from "@/components/enquiry-dialog";
import { PaymentButton } from "@/components/payments/payment-button";
import { format, addDays, subDays } from "date-fns";
import { cn } from "@/lib/utils";
import { ScheduleVisitWidget } from "@/components/schedule-visit-widget";
import { NationalitySelect } from "@/components/common/NationalitySelect";
import { RichTextContent } from "@/components/shared/RichTextContent";
import { isRestrictedNationality } from "@/lib/nationality";
import { Calendar } from "@/components/ui/calendar";
import { useSearchParams } from "next/navigation";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { PhoneInput } from "@/components/ui/phone-input";
import { Label } from "@/components/ui/label";
import {
  resolvePropertyImageUrl,
  shouldUnoptimizedPropertyImage,
} from "@/lib/property-image";

interface WebsiteRuleGroups {
  stay_rule: string[];
  cancellation_rule: string[];
  other: string[];
}

interface WebsiteFaq {
  id: string;
  question: string;
  answer: string;
  category?: string;
}

const DEFAULT_STAY_RULES = [
  "Check-in after 4:00 PM",
  "Checkout before 11:00 AM",
  "No smoking inside the property",
  "No parties or events",
];

const DEFAULT_CANCELLATION_RULES = [
  "If 30+ days before move-in, 100% refund (excluding any card processing fees).",
  "If 14-29 days before move-in, 50% refund (excluding any card processing fees).",
];

const API_BASE_URL =
  process.env.NEXT_PUBLIC_API_URL ||
  process.env.NEXT_PUBLIC_CMS_API_URL ||
  "http://localhost:3001";
const MIN_PLATFORM_STAY_DAYS = 10;
const AVAILABILITY_SOON_DAYS = 60;

function Image({ src, unoptimized, ...props }: ImageProps) {
  const shouldUseUnoptimized =
    typeof src === "string" ? shouldUnoptimizedPropertyImage(src) : false;

  return (
    <NextImage
      {...props}
      src={src}
      unoptimized={unoptimized ?? shouldUseUnoptimized}
    />
  );
}

export interface TripData {
  title: string;
  images: string[];
  summary: {
    location: string;
    propertyType: string;
    bedrooms: string;
    bathrooms?: string;
    area?: string;
    parking?: string;
  };
  highlights: { icon: string; text: string }[];
  description: string;
  /** Sanitized rich-text HTML from the CMS; falls back to `description` when absent. */
  descriptionHtml?: string;
  propertyRooms: {
    room: string;
    description: string;
    images: string[];
    amenities: string[];
  }[];
  insideAmenities: { icon: string; text: string }[];
  outsideAmenities: { icon: string; text: string }[];
  amenities?: string[];
  availableFor: { icon: string; label: string; description: string }[];
  essentials: { icon: string; label: string }[];
  bookingRules?: {
    stay: string[];
    cancellation: string[];
    other?: string[];
  };
  faqs: string[];
  pricing: {
    from: number;
    currency: string;
    rentType?: string;
    fees?: {
      vat: number;
      bookingFee: number;
      exitCleaningFee: number;
      tourismFee: number;
      securityDeposit: number;
    };
    availableDates: {
      dates: string;
      price: number;
      available: boolean;
      selected: boolean;
    }[];
  };
  property_video?: string | null;
  type_of_room?: string | null;
  available_from?: string | null;
}

interface RentalData {
  status: string;
  currentRentalEnd: string | null;
  currentRentalStart: string | null;
  rentalPeriods: any[];
}

interface TripExperienceClientProps {
  trip: TripData;
  slug: string;
  id: string;
  /** Clean URL segment (title slug) for building sub-links without the UUID. */
  pathSegment?: string;
  rentalData?: RentalData | null;
  similarProperties?: any[];
}

export default function TripExperienceClient({
  trip: rawTrip,
  slug,
  id,
  pathSegment,
  rentalData,
  similarProperties = [],
}: TripExperienceClientProps) {
  // Segment used for on-page links (keeps permalinks clean); falls back to the UUID.
  const linkSegment = pathSegment || id;
  const PENDING_BOOKING_ATTEMPT_KEY = "hopohomes_pending_booking_attempt";
  const searchParams = useSearchParams();

  const trip = useMemo(() => {
    const rawImages = Array.isArray(rawTrip.images) ? rawTrip.images : [];
    const resolvedImages = rawImages
      .map((image, imageIndex) =>
        resolvePropertyImageUrl(image, {
          propertyId: id,
          imageIndex,
        }),
      )
      .filter(Boolean);

    return {
      ...rawTrip,
      images: resolvedImages,
    };
  }, [rawTrip, id]);

  const [apiRules, setApiRules] = useState<WebsiteRuleGroups | null>(null);
  const [apiFaqs, setApiFaqs] = useState<WebsiteFaq[]>([]);
  const [expandedRoom, setExpandedRoom] = useState<string | null>(null);
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);
  const [showAllAmenities, setShowAllAmenities] = useState(false);
  const [expandedFAQ, setExpandedFAQ] = useState<string | null>(null);
  const [selectedDateIndex, setSelectedDateIndex] = useState<number>(0);
  const [showPaymentSuccess, setShowPaymentSuccess] = useState(false);
  const [paymentSuccessData, setPaymentSuccessData] = useState<any>(null);

  // Check for payment success on mount
  useEffect(() => {
    const paymentStatus = searchParams.get("payment");
    if (paymentStatus === "success") {
      // Get payment data from sessionStorage
      const storedData = sessionStorage.getItem("hopohomes_payment_success");
      if (storedData) {
        try {
          const data = JSON.parse(storedData);
          setPaymentSuccessData(data);
          setShowPaymentSuccess(true);
          // Clear the stored data
          sessionStorage.removeItem("hopohomes_payment_success");

          // Auto-hide success banner after 10 seconds
          setTimeout(() => setShowPaymentSuccess(false), 10000);
        } catch (e) {
          console.error("Failed to parse payment success data:", e);
        }
      } else {
        // Still show success banner even without stored data
        setShowPaymentSuccess(true);
        setTimeout(() => setShowPaymentSuccess(false), 10000);
      }
    }
  }, [searchParams]);

  // Fetch FAQs (prefer properties category, fallback to all)
  useEffect(() => {
    let mounted = true;

    const parseFaqs = (raw: any): WebsiteFaq[] => {
      if (!Array.isArray(raw)) return [];

      return raw
        .filter(
          (item: any) =>
            item &&
            typeof item.question === "string" &&
            typeof item.answer === "string" &&
            item.is_active !== false,
        )
        .map((item: any, index: number) => ({
          id: String(item.id || `faq-${index}`),
          question: String(item.question || "").trim(),
          answer: String(item.answer || "").trim(),
          category:
            typeof item.category === "string" ? item.category : undefined,
        }))
        .filter((item) => item.question.length > 0);
    };

    const loadFaqs = async () => {
      try {
        // Fetch all active FAQs without category restriction to ensure all are displayed
        const response = await fetch(`${API_BASE_URL}/api/faqs`);
        if (response.ok) {
          const result = await response.json();
          const faqs = parseFaqs(result?.data);
          if (mounted) setApiFaqs(faqs);
        }
      } catch (error) {
        console.error("Error loading FAQs:", error);
        if (mounted) setApiFaqs([]);
      }
    };

    loadFaqs();

    return () => {
      mounted = false;
    };
  }, []);

  // Fetch latest active property rules for this page section
  useEffect(() => {
    let mounted = true;

    const loadRules = async () => {
      try {
        const response = await fetch(
          `${API_BASE_URL}/api/property-rules?status=active`,
        );
        if (!response.ok) return;

        const result = await response.json();
        if (!result?.success || !result?.grouped) return;

        if (!mounted) return;
        setApiRules({
          stay_rule: Array.isArray(result.grouped.stay_rule)
            ? result.grouped.stay_rule
            : [],
          cancellation_rule: Array.isArray(result.grouped.cancellation_rule)
            ? result.grouped.cancellation_rule
            : [],
          other: Array.isArray(result.grouped.other)
            ? result.grouped.other
            : [],
        });
      } catch {
        // Keep server-provided trip.bookingRules as fallback.
      }
    };

    loadRules();

    return () => {
      mounted = false;
    };
  }, []);

  const cancellationRules = apiRules?.cancellation_rule?.length
    ? apiRules.cancellation_rule
    : trip.bookingRules?.cancellation?.length
      ? trip.bookingRules.cancellation
      : DEFAULT_CANCELLATION_RULES;

  const stayRules = apiRules?.stay_rule?.length
    ? apiRules.stay_rule
    : trip.bookingRules?.stay?.length
      ? trip.bookingRules.stay
      : DEFAULT_STAY_RULES;

  const otherRules = apiRules?.other?.length
    ? apiRules.other
    : trip.bookingRules?.other || [];

  const normalizedRentType = String(trip.pricing.rentType || "").toLowerCase();
  const rentTypeDisplay =
    normalizedRentType === "annual"
      ? "/Yearly"
      : normalizedRentType === "monthly"
        ? "/Monthly"
        : "";

  // Yearly listings: online "Book Now" is disabled (large annual contracts are rarely
  // paid fully online); only "Schedule Visit" is offered for these.
  const isYearly = normalizedRentType === "annual";

  const fallbackFaqs: WebsiteFaq[] = (trip.faqs || [])
    .filter(
      (question) => typeof question === "string" && question.trim().length > 0,
    )
    .map((question, index) => ({
      id: `fallback-faq-${index}`,
      question: question.trim(),
      answer: "Please contact our support team for details on this question.",
    }));

  const displayedFaqs = apiFaqs.length > 0 ? apiFaqs : fallbackFaqs;

  // Calculate minimum check-in date based on rental status
  const isFutureAvailable = (() => {
    if (rentalData?.status === "waitlist") return true;
    if (rentalData?.status === "available") return false;

    if (!trip.available_from) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const avail = new Date(trip.available_from);
    avail.setHours(0, 0, 0, 0);
    return avail > today;
  })();

  const getMinimumCheckInDate = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (trip.available_from) {
      const availableDate = new Date(trip.available_from);
      availableDate.setHours(0, 0, 0, 0);
      if (availableDate > today) return availableDate;
    }

    return today;
  };

  const effectiveRentalPeriods = (() => {
    const periods = Array.isArray(rentalData?.rentalPeriods)
      ? [...rentalData.rentalPeriods]
      : [];

    if (rentalData?.currentRentalStart && rentalData?.currentRentalEnd) {
      const alreadyExists = periods.some(
        (period: any) =>
          period?.start_date === rentalData.currentRentalStart &&
          period?.end_date === rentalData.currentRentalEnd &&
          (period?.status === "active" || period?.status === "confirmed"),
      );

      if (!alreadyExists) {
        periods.push({
          start_date: rentalData.currentRentalStart,
          end_date: rentalData.currentRentalEnd,
          status: "active",
        });
      }
    }

    return periods;
  })();

  // Check if a date falls within any booked period
  const isDateBooked = (date: Date): boolean => {
    const checkDate = new Date(date);
    checkDate.setHours(0, 0, 0, 0);

    // Block dates before available_from
    if (trip.available_from) {
      const availableDate = new Date(trip.available_from);
      availableDate.setHours(0, 0, 0, 0);
      if (checkDate < availableDate) return true;
    }

    if (!rentalData || effectiveRentalPeriods.length === 0) return false;

    return !!effectiveRentalPeriods
      .filter(
        (period: any) =>
          period.status === "active" || period.status === "confirmed",
      )
      .find((period: any) => {
        const startDate = new Date(period.start_date);
        const endDate = new Date(period.end_date);
        startDate.setHours(0, 0, 0, 0);
        endDate.setHours(0, 0, 0, 0);

        return checkDate >= startDate && checkDate <= endDate;
      });
  };

  // Get all active/confirmed booked periods sorted by start date
  const bookedPeriods = (() => {
    if (!rentalData || effectiveRentalPeriods.length === 0) return [];
    return effectiveRentalPeriods
      .filter(
        (period: any) =>
          period.status === "active" || period.status === "confirmed",
      )
      .map((period: any) => ({
        start: new Date(period.start_date),
        end: new Date(period.end_date),
        tenant_name: period.tenant_name,
        status: period.status,
      }))
      .sort((a: any, b: any) => a.start.getTime() - b.start.getTime());
  })();

  const minimumDate = getMinimumCheckInDate();
  const [checkInDate, setCheckInDate] = useState<Date | undefined>(minimumDate);
  const [checkOutDate, setCheckOutDate] = useState<Date | undefined>(
    addDays(minimumDate, MIN_PLATFORM_STAY_DAYS),
  );
  const [guests, setGuests] = useState(3);
  const [isSaved, setIsSaved] = useState(false);
  const [selectedCancellation, setSelectedCancellation] = useState<
    "non-refundable" | "refundable"
  >("non-refundable");
  const [bookingData, setBookingData] = useState<any>(null);

  // Calculate booking details
  const nights =
    checkInDate && checkOutDate
      ? Math.max(
          1,
          Math.ceil(
            (checkOutDate.getTime() - checkInDate.getTime()) /
              (1000 * 60 * 60 * 24),
          ),
        )
      : 30;

  const isShortStaySelection = nights < MIN_PLATFORM_STAY_DAYS;

  const isMonthlyView = false;

  const vatRate = trip.pricing.fees?.vat || 5;
  // trip.pricing.from is the listing's headline price in its own unit (monthly or yearly).
  // When the listing is priced yearly, the monthly-equivalent is year / 12 — previously the
  // yearly figure was treated as monthly and divided by 30, producing a ~12x-inflated nightly rate.
  const rentPerMonth =
    normalizedRentType === "annual"
      ? trip.pricing.from / 12
      : trip.pricing.from;
  const rentPerNight = rentPerMonth / 30;

  const rentAmount = Math.round(rentPerNight * nights);
  const vatAmount = Math.round(rentAmount * (vatRate / 100));
  const rentTotal = rentAmount + vatAmount;

  // Security deposit: for annually-priced listings the stored deposit is an annual-scale
  // figure (typically ~5% of the yearly rent), which is far too high for a monthly/short-stay
  // booking. Scale it to the monthly context the same way the rent is (yearly / 12); monthly
  // listings keep their stored flat deposit as-is.
  const securityDeposit =
    normalizedRentType === "annual"
      ? Math.round((trip.pricing.fees?.securityDeposit || 0) / 12)
      : trip.pricing.fees?.securityDeposit || 0;

  const feesTotal =
    (trip.pricing.fees?.bookingFee || 0) +
    (trip.pricing.fees?.exitCleaningFee || 0) +
    (trip.pricing.fees?.tourismFee || 0) +
    securityDeposit;

  const grandTotal = rentTotal + feesTotal;

  // Check if TODAY is within an active rental period
  const isCurrentlyRentedNow = (() => {
    if (!rentalData || effectiveRentalPeriods.length === 0) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return effectiveRentalPeriods.some((period: any) => {
      if (period.status !== "active" && period.status !== "confirmed")
        return false;
      const pStart = new Date(period.start_date);
      pStart.setHours(0, 0, 0, 0);
      const pEnd = new Date(period.end_date);
      pEnd.setHours(0, 0, 0, 0);
      return today >= pStart && today <= pEnd;
    });
  })();

  // Check if property has any upcoming (future) bookings
  const hasUpcomingBookings = (() => {
    if (!rentalData || effectiveRentalPeriods.length === 0) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return effectiveRentalPeriods.some((period: any) => {
      if (period.status !== "active" && period.status !== "confirmed")
        return false;
      const pEnd = new Date(period.end_date);
      pEnd.setHours(0, 0, 0, 0);
      return pEnd >= today; // booking is still active or in the future
    });
  })();

  // Get the next truly available date considering all current + future bookings
  // and ensuring we have a gap of at least 10 days.
  const nextAvailableFromDate = (() => {
    if (!rentalData || effectiveRentalPeriods.length === 0) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const sortedPeriods = effectiveRentalPeriods
      .filter((period: any) => {
        if (period.status !== "active" && period.status !== "confirmed")
          return false;
        const pEnd = new Date(period.end_date);
        pEnd.setHours(0, 0, 0, 0);
        return pEnd >= today;
      })
      .map((period: any) => {
        const s = new Date(period.start_date);
        s.setHours(0, 0, 0, 0);
        const e = new Date(period.end_date);
        e.setHours(0, 0, 0, 0);
        return { start: s, end: e };
      })
      .sort((a: any, b: any) => a.start.getTime() - b.start.getTime());

    if (sortedPeriods.length === 0) return null;

    let searchStart = new Date(today);

    for (const period of sortedPeriods) {
      if (period.end < searchStart) continue;

      const gapMs = period.start.getTime() - searchStart.getTime();
      const daysGap = Math.round(gapMs / (1000 * 60 * 60 * 24));

      if (daysGap >= 10) {
        // Found a valid availability window of at least 10 days
        break;
      }

      // Move searchStart to the day after this period ends
      if (period.end >= searchStart) {
        const nextDay = new Date(period.end);
        nextDay.setDate(nextDay.getDate() + 1);
        if (nextDay > searchStart) {
          searchStart = nextDay;
        }
      }
    }

    // If the next available block of 10+ days starts today, we don't display "Available From"
    if (searchStart.getTime() === today.getTime()) {
      return null;
    }

    return searchStart;
  })();

  const daysUntilNextAvailability = (() => {
    if (!nextAvailableFromDate) return 0;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const date = new Date(nextAvailableFromDate);
    date.setHours(0, 0, 0, 0);
    return Math.max(
      0,
      Math.ceil((date.getTime() - today.getTime()) / (1000 * 60 * 60 * 24)),
    );
  })();

  const isWaitlistOnlyAvailability = Boolean(
    rentalData?.status === "waitlist" ||
    (rentalData?.status !== "available" &&
      nextAvailableFromDate &&
      daysUntilNextAvailability > AVAILABILITY_SOON_DAYS),
  );

  const isSelectedWaitlist = Boolean(
    checkInDate &&
    (() => {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const cIn = new Date(checkInDate);
      cIn.setHours(0, 0, 0, 0);
      return (
        Math.ceil((cIn.getTime() - today.getTime()) / (1000 * 60 * 60 * 24)) >
        AVAILABILITY_SOON_DAYS
      );
    })(),
  );

  const showWaitlistForm = isWaitlistOnlyAvailability || isSelectedWaitlist;

  // Compute conflicting booking based on currently selected check-in/out dates
  const conflictingBooking = (() => {
    if (
      !checkInDate ||
      !checkOutDate ||
      !rentalData ||
      effectiveRentalPeriods.length === 0
    )
      return null;
    const cIn = new Date(checkInDate);
    cIn.setHours(0, 0, 0, 0);
    const cOut = new Date(checkOutDate);
    cOut.setHours(0, 0, 0, 0);
    return (
      effectiveRentalPeriods.find((period: any) => {
        if (period.status !== "active" && period.status !== "confirmed")
          return false;
        const pStart = new Date(period.start_date);
        pStart.setHours(0, 0, 0, 0);
        const pEnd = new Date(period.end_date);
        pEnd.setHours(0, 0, 0, 0);
        return cIn <= pEnd && cOut >= pStart;
      }) ?? null
    );
  })();

  const handleCheckInSelect = (date: Date | undefined) => {
    setCheckInDate(date);
    if (date) {
      // Automatically select check-out date as 10 days later
      const minCheckOutDate = addDays(date, MIN_PLATFORM_STAY_DAYS);
      if (!checkOutDate || checkOutDate < minCheckOutDate) {
        setCheckOutDate(minCheckOutDate);
      }
    }
  };

  const [isEnquiryOpen, setIsEnquiryOpen] = useState(false);
  const [enquiryFlow, setEnquiryFlow] = useState<"waitlist" | "short_stay">(
    "waitlist",
  );
  const [isMobileDrawerOpen, setIsMobileDrawerOpen] = useState(false);
  const [isPriceDetailsOpen, setIsPriceDetailsOpen] = useState(false);
  const [isPaymentTimelineOpen, setIsPaymentTimelineOpen] = useState(false);
  const [isPaymentDialogOpen, setIsPaymentDialogOpen] = useState(false);
  const [customerInfo, setCustomerInfo] = useState({
    name: "",
    email: "",
    phone: "",
    nationality: "",
  });

  // When a restricted-nationality visitor fills in their details, record their interest
  // once (best-effort) so the team can follow up — this backs the "we'll get back to you"
  // message shown in place of the payment options.
  const restrictedLeadSentRef = useRef<string | null>(null);
  useEffect(() => {
    const { name, email, phone, nationality } = customerInfo;
    if (!isRestrictedNationality(nationality) || !name.trim() || !email.trim())
      return;
    const key = `${email.trim().toLowerCase()}|${nationality}`;
    if (restrictedLeadSentRef.current === key) return;
    restrictedLeadSentRef.current = key;
    fetch("/api/enquiries", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        type: "restricted_nationality",
        name,
        email,
        phone: phone || "",
        message: `Interested but online booking is not offered for nationality: ${nationality}.`,
        propertyId: id,
        propertyTitle: trip.title,
        propertyDetails: {
          nationality,
          propertyLocation: trip.summary.location,
          source: "booking_nationality_gate",
        },
      }),
    }).catch(() => {
      /* best-effort */
    });
  }, [customerInfo, id, trip.title, trip.summary.location]);

  // Already Paid flow states
  const [paymentMode, setPaymentMode] = useState<"card" | "offline" | null>(
    null,
  );
  const [invoiceFile, setInvoiceFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState("");
  const [uploadSuccess, setUploadSuccess] = useState(false);

  // Image zoom modal state
  const [isZoomModalOpen, setIsZoomModalOpen] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [failedImages, setFailedImages] = useState<Set<number>>(new Set());

  // Share Dialog state
  const [isShareDialogOpen, setIsShareDialogOpen] = useState(false);
  const [isCopied, setIsCopied] = useState(false);

  const openWaitlistDialog = () => {
    setEnquiryFlow("waitlist");
    setIsEnquiryOpen(true);
  };

  const openShortStayDialog = () => {
    setEnquiryFlow("short_stay");
    setIsEnquiryOpen(true);
  };

  const persistPendingBookingAttempt = async (session: {
    orderNumber: string;
    paymentId?: string;
    amount: number;
    customerEmail: string;
    customerName: string;
    customerPhone?: string;
    checkInDate?: string;
    checkOutDate?: string;
    description?: string;
  }) => {
    try {
      sessionStorage.setItem(
        PENDING_BOOKING_ATTEMPT_KEY,
        JSON.stringify({
          orderNumber: session.orderNumber,
          paymentId: session.paymentId,
          propertyId: id,
          propertyTitle: trip.title,
          propertyLocation: trip.summary.location,
          amount: session.amount,
          customerEmail: session.customerEmail,
          customerName: session.customerName,
          customerPhone: session.customerPhone,
          checkInDate: session.checkInDate,
          checkOutDate: session.checkOutDate,
          description: session.description,
          attemptedAt: new Date().toISOString(),
        }),
      );
    } catch (error) {
      console.warn("Failed to persist pending booking attempt:", error);
    }
  };

  useEffect(() => {
    const paymentStatus = searchParams.get("payment");

    if (paymentStatus === "success") {
      sessionStorage.removeItem(PENDING_BOOKING_ATTEMPT_KEY);
      return;
    }

    const pendingRaw = sessionStorage.getItem(PENDING_BOOKING_ATTEMPT_KEY);
    if (!pendingRaw) return;

    let pendingAttempt: any = null;
    try {
      pendingAttempt = JSON.parse(pendingRaw);
    } catch {
      sessionStorage.removeItem(PENDING_BOOKING_ATTEMPT_KEY);
      return;
    }

    if (!pendingAttempt?.propertyId || pendingAttempt.propertyId !== id) {
      return;
    }

    // Remove first to avoid duplicate inserts caused by re-renders/strict mode.
    sessionStorage.removeItem(PENDING_BOOKING_ATTEMPT_KEY);

    const submitAttempt = async () => {
      try {
        const response = await fetch("/api/enquiries", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            type: "property_booking_attempt",
            name: pendingAttempt.customerName,
            email: pendingAttempt.customerEmail,
            phone: pendingAttempt.customerPhone || "",
            message:
              "Customer initiated payment for booking but did not complete checkout.",
            propertyId: pendingAttempt.propertyId,
            propertyTitle: pendingAttempt.propertyTitle || trip.title,
            propertyDetails: {
              bookingType: "payment_attempt",
              paymentStatus: "abandoned",
              orderNumber: pendingAttempt.orderNumber,
              paymentId: pendingAttempt.paymentId,
              propertyLocation: pendingAttempt.propertyLocation,
              amount: pendingAttempt.amount,
              checkInDate: pendingAttempt.checkInDate,
              checkOutDate: pendingAttempt.checkOutDate,
              attemptedAt: pendingAttempt.attemptedAt,
              source: "payment_redirect_return",
            },
          }),
        });

        if (!response.ok) {
          sessionStorage.setItem(PENDING_BOOKING_ATTEMPT_KEY, pendingRaw);
        }
      } catch {
        sessionStorage.setItem(PENDING_BOOKING_ATTEMPT_KEY, pendingRaw);
      }
    };

    submitAttempt();
  }, [id, searchParams, trip.summary.location, trip.title]);

  const handleProceedToPayment = async () => {
    setPaymentMode("card");

    try {
      fetch("/api/enquiries", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          type: "property_booking_attempt",
          name: customerInfo.name,
          email: customerInfo.email,
          phone: customerInfo.phone || "",
          message:
            'Customer clicked "Proceed to Payment" but did not initiate TotalPay checkout.',
          propertyId: id,
          propertyTitle: trip.title,
          propertyDetails: {
            bookingType: "payment_attempt",
            paymentStatus: "abandoned_before_checkout",
            orderNumber: null,
            paymentId: null,
            propertyLocation: trip.summary.location,
            amount: grandTotal,
            checkInDate: checkInDate?.toISOString().split("T")[0],
            checkOutDate: checkOutDate?.toISOString().split("T")[0],
            attemptedAt: new Date().toISOString(),
            source: "proceed_to_payment_click",
          },
        }),
      }).catch((err) => console.warn("Failed to log proceed attempt:", err));
    } catch {
      // Background request, no UI disruption on fail
    }
  };

  const handleAlreadyPaidSubmit = async () => {
    if (!invoiceFile) {
      setUploadError("Please select an invoice file to upload");
      return;
    }
    if (!customerInfo.name || !customerInfo.email) {
      setUploadError("Please fill your name and email above");
      return;
    }

    try {
      setIsUploading(true);
      setUploadError("");

      // 1. Convert file to base64
      const reader = new FileReader();
      reader.readAsDataURL(invoiceFile);

      const fileBase64 = await new Promise<string>((resolve, reject) => {
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = reject;
      });

      const API_URL =
        process.env.NEXT_PUBLIC_CMS_API_URL || "http://localhost:3001";

      // 2. Upload to storage
      const storageRes = await fetch(`${API_URL}/api/storage/upload`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          file: {
            data: fileBase64,
            name: invoiceFile.name,
            type: invoiceFile.type,
          },
          folderName: "invoices",
        }),
      });

      const storageData = await storageRes.json();
      if (!storageRes.ok || !storageData.success) {
        throw new Error(storageData.error || "Failed to upload invoice");
      }

      const invoiceUrl = storageData.data.url;

      // 3. Create manual payment record
      const paymentRes = await fetch(`${API_URL}/api/payments/manual`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          propertyId: id,
          amount: grandTotal,
          customerEmail: customerInfo.email,
          customerName: customerInfo.name,
          customerPhone: customerInfo.phone,
          checkInDate: checkInDate?.toISOString().split("T")[0],
          checkOutDate: checkOutDate?.toISOString().split("T")[0],
          description: `Booking for ${trip.title}`,
          invoiceUrl,
        }),
      });

      const paymentData = await paymentRes.json();
      if (!paymentRes.ok || !paymentData.success) {
        throw new Error(
          paymentData.error || "Failed to process offline payment",
        );
      }

      setUploadSuccess(true);
      setTimeout(() => {
        setIsPaymentDialogOpen(false);
        setUploadSuccess(false);
        setPaymentMode(null);
        setInvoiceFile(null);
        // Show success banner
        setPaymentSuccessData({
          order_number: paymentData.order_number,
          amount: grandTotal,
          customerName: customerInfo.name,
          manual_verification: true,
        });
        setShowPaymentSuccess(true);
      }, 2000);
    } catch (err: any) {
      console.error("File upload error:", err);
      setUploadError(err.message || "Error uploading file");
    } finally {
      setIsUploading(false);
    }
  };

  const handleCopyLink = () => {
    const url = window.location.href;
    navigator.clipboard.writeText(url).then(() => {
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    });
  };

  const handleEmailApartment = () => {
    const subject = encodeURIComponent(`Inquiry about ${trip.title}`);
    const body = encodeURIComponent(
      `Hi,\n\nI'm interested in this property: ${trip.title}\nLocation: ${trip.summary.location}\nLink: ${window.location.href}\n\nPlease let me know if it's available.\n\nThanks!`,
    );
    window.location.href = `mailto:info@hopohomes.com?subject=${subject}&body=${body}`;
  };

  // Open zoom modal with selected image
  const openZoomModal = (index: number) => {
    setSelectedImageIndex(index);
    setIsZoomModalOpen(true);
  };

  // Navigate to previous image
  const goToPreviousImage = () => {
    setSelectedImageIndex((prev) =>
      prev === 0 ? trip.images.length - 1 : prev - 1,
    );
  };

  // Navigate to next image
  const goToNextImage = () => {
    setSelectedImageIndex((prev) =>
      prev === trip.images.length - 1 ? 0 : prev + 1,
    );
  };

  // Handle image loading errors
  const handleImageError = (index: number) => {
    console.error(
      `Failed to load image at index ${index}:`,
      trip.images[index],
    );
    setFailedImages((prev) => new Set(prev).add(index));
  };

  // Handle keyboard navigation
  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isZoomModalOpen) return;
      if (e.key === "Escape") setIsZoomModalOpen(false);
      if (e.key === "ArrowLeft") goToPreviousImage();
      if (e.key === "ArrowRight") goToNextImage();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isZoomModalOpen]);

  const toggleRoom = (roomName: string) => {
    setExpandedRoom(expandedRoom === roomName ? null : roomName);
  };

  const toggleDescription = () => {
    setIsDescriptionExpanded(!isDescriptionExpanded);
  };

  const toggleFAQ = (faqId: string) => {
    setExpandedFAQ(expandedFAQ === faqId ? null : faqId);
  };

  const selectDateOption = (index: number) => {
    setSelectedDateIndex(index);
  };

  return (
    <>
      {/* Payment Success Banner */}
      {showPaymentSuccess && (
        <div className="fixed top-4 left-1/2 -translate-x-1/2 z-[100] animate-in slide-in-from-top duration-300">
          <div className="bg-green-500 text-white px-6 py-4 rounded-xl shadow-2xl max-w-md flex items-start gap-3 border-2 border-green-400">
            <CheckCircle2 className="w-6 h-6 shrink-0 mt-0.5" />
            <div className="flex-1">
              <h3 className="font-bold text-lg mb-1">
                {paymentSuccessData?.manual_verification
                  ? "Receipt Submitted! 📝"
                  : "Payment Successful! 🎉"}
              </h3>
              <p className="text-sm text-green-50">
                {paymentSuccessData?.manual_verification
                  ? "We have received your payment receipt. We will verify and confirm your booking shortly."
                  : "Your booking has been confirmed. We will contact you shortly with all the details."}
              </p>
              {paymentSuccessData && (
                <div className="mt-2 text-xs text-green-100 space-y-0.5">
                  <p>Order: {paymentSuccessData.order_number}</p>
                  <p>
                    Amount: {paymentSuccessData.currency}{" "}
                    {Number(paymentSuccessData.amount).toFixed(2)}
                  </p>
                </div>
              )}
            </div>
            <button
              onClick={() => setShowPaymentSuccess(false)}
              className="shrink-0 p-1 hover:bg-green-600 rounded-full transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}

      {/* Image Zoom Modal */}
      {isZoomModalOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center"
          onClick={() => setIsZoomModalOpen(false)}
        >
          {/* Close Button */}
          <button
            onClick={() => setIsZoomModalOpen(false)}
            className="absolute top-4 right-4 z-50 p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
          >
            <X className="w-6 h-6 text-white" />
          </button>

          {/* Image Counter */}
          <div className="absolute top-4 left-4 z-50 text-white text-sm font-medium bg-black/50 px-3 py-1.5 rounded-full">
            {selectedImageIndex + 1} / {trip.images.length}
          </div>

          {/* Previous Button */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              goToPreviousImage();
            }}
            className="absolute left-4 z-50 p-3 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
          >
            <ChevronLeft className="w-8 h-8 text-white" />
          </button>

          {/* Main Image */}
          <div
            className="relative w-full h-full max-w-5xl max-h-[80vh] mx-16"
            onClick={(e) => e.stopPropagation()}
          >
            {failedImages.has(selectedImageIndex) ? (
              <div className="flex items-center justify-center h-full">
                <div className="text-center text-white">
                  <ImageOff className="w-16 h-16 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">Failed to load image</p>
                </div>
              </div>
            ) : (
              <Image
                src={trip.images[selectedImageIndex]}
                alt={`Property image ${selectedImageIndex + 1}`}
                title={`Property image ${selectedImageIndex + 1}`}
                fill
                className="object-contain"
                priority
                onError={() => handleImageError(selectedImageIndex)}
              />
            )}
          </div>

          {/* Next Button */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              goToNextImage();
            }}
            className="absolute right-4 z-50 p-3 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
          >
            <ChevronRight className="w-8 h-8 text-white" />
          </button>

          {/* Thumbnail Strip */}
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 p-2 bg-black/50 rounded-xl max-w-[90vw] overflow-x-auto">
            {trip.images.map((image, index) => (
              <button
                key={index}
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedImageIndex(index);
                }}
                className={`relative w-16 h-12 rounded-lg overflow-hidden flex-shrink-0 transition-all ${
                  selectedImageIndex === index
                    ? "ring-2 ring-white scale-105"
                    : "opacity-60 hover:opacity-100"
                }`}
              >
                {failedImages.has(index) ? (
                  <div className="flex items-center justify-center w-full h-full bg-muted">
                    <ImageOff className="w-4 h-4 text-muted-foreground" />
                  </div>
                ) : (
                  <Image
                    src={image}
                    alt={`Thumbnail ${index + 1}`}
                    title={`Thumbnail ${index + 1}`}
                    fill
                    className="object-cover"
                    onError={() => handleImageError(index)}
                  />
                )}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="max-w-[1400px] w-full mx-auto px-4 xl:px-6 py-8 relative flex flex-col">
        {/* Two Column Layout - Gallery Left, Sidebar Right */}
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Left Column - Gallery & Content */}
          <div className="flex-1 flex flex-col min-w-0 pt-[4rem]">
            {/* Image Gallery - Dynamic Layout Based on Image Count */}
            <div className="flex overflow-hidden relative h-[380px] rounded-2xl gap-2">
              {/* No Images State */}
              {trip.images.length === 0 && (
                <div className="w-full h-full bg-muted flex flex-col items-center justify-center gap-3">
                  <div className="w-16 h-16 rounded-full bg-background flex items-center justify-center shadow-sm">
                    <Camera className="w-8 h-8 text-muted-foreground opacity-40" />
                  </div>
                  <div className="text-center">
                    <h3 className="font-semibold text-lg text-foreground">
                      No photos available
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      This property doesn't have any images yet.
                    </p>
                  </div>
                </div>
              )}

              {trip.images.length > 0 && (
                <>
                  {/* Desktop Gallery - Dynamic based on image count */}
                  <div className="hidden w-full h-full lg:flex gap-2">
                    {/* 1 Image Layout */}
                    {trip.images.length === 1 && (
                      <div
                        className="w-full relative rounded-2xl overflow-hidden"
                        onClick={() => !failedImages.has(0) && openZoomModal(0)}
                      >
                        {failedImages.has(0) ? (
                          <div className="flex items-center justify-center w-full h-full bg-muted">
                            <div className="text-center">
                              <ImageOff className="w-12 h-12 mx-auto mb-2 text-muted-foreground" />
                              <p className="text-sm text-muted-foreground">
                                Failed to load image
                              </p>
                            </div>
                          </div>
                        ) : (
                          <Image
                            src={trip.images[0]}
                            alt="Property image"
                            title="Property image"
                            fill
                            className="object-cover cursor-pointer hover:scale-105 transition-transform duration-300"
                            onError={() => handleImageError(0)}
                          />
                        )}
                        <Link
                          href={`/properties/${slug}/${linkSegment}/photo`}
                          className="absolute bottom-4 right-4 bg-white/90 backdrop-blur-sm px-4 py-2 rounded-full flex items-center gap-2 text-black font-medium hover:bg-white transition-colors"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Camera className="w-4 h-4" />
                          <span>View photos</span>
                        </Link>
                      </div>
                    )}

                    {/* 2 Images Layout - Side by side */}
                    {trip.images.length === 2 && (
                      <>
                        <div
                          className="w-1/2 relative rounded-xl overflow-hidden"
                          onClick={() =>
                            !failedImages.has(0) && openZoomModal(0)
                          }
                        >
                          {failedImages.has(0) ? (
                            <div className="flex items-center justify-center w-full h-full bg-muted">
                              <ImageOff className="w-8 h-8 text-muted-foreground" />
                            </div>
                          ) : (
                            <Image
                              src={trip.images[0]}
                              alt="Property image 1"
                              title="Property image 1"
                              fill
                              sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 400px"
                              className="object-cover cursor-pointer hover:scale-105 transition-transform duration-300"
                              onError={() => handleImageError(0)}
                            />
                          )}
                        </div>
                        <div
                          className="w-1/2 relative rounded-xl overflow-hidden"
                          onClick={() =>
                            !failedImages.has(1) && openZoomModal(1)
                          }
                        >
                          {failedImages.has(1) ? (
                            <div className="flex items-center justify-center w-full h-full bg-muted">
                              <ImageOff className="w-8 h-8 text-muted-foreground" />
                            </div>
                          ) : (
                            <Image
                              src={trip.images[1]}
                              alt="Property image 2"
                              title="Property image 2"
                              fill
                              sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 400px"
                              className="object-cover cursor-pointer hover:scale-105 transition-transform duration-300"
                              onError={() => handleImageError(1)}
                            />
                          )}
                          <button
                            type="button"
                            title="See all photos"
                            className="absolute inset-0 bg-black/40 flex items-center justify-center gap-2 text-white font-medium hover:bg-black/50 transition-colors"
                            onClick={(e) => {
                              e.stopPropagation();
                              openZoomModal(0);
                            }}
                          >
                            <Camera className="w-4 h-4" />
                            <span>See all</span>
                          </button>
                        </div>
                      </>
                    )}

                    {/* 3 Images Layout - 1 large left + 2 stacked right (like reference image) */}
                    {trip.images.length === 3 && (
                      <>
                        <div
                          className="w-1/2 relative rounded-xl overflow-hidden"
                          onClick={() =>
                            !failedImages.has(0) && openZoomModal(0)
                          }
                        >
                          {failedImages.has(0) ? (
                            <div className="flex items-center justify-center w-full h-full bg-muted">
                              <ImageOff className="w-8 h-8 text-muted-foreground" />
                            </div>
                          ) : (
                            <Image
                              src={trip.images[0]}
                              alt="Main property image"
                              title="Main property image"
                              fetchPriority="high"
                              fill
                              className="object-cover cursor-pointer hover:scale-105 transition-transform duration-300"
                              onError={() => handleImageError(0)}
                            />
                          )}
                        </div>
                        <div className="w-1/2 flex flex-col gap-2">
                          <div
                            className="flex-1 relative rounded-xl overflow-hidden"
                            onClick={() =>
                              !failedImages.has(1) && openZoomModal(1)
                            }
                          >
                            {failedImages.has(1) ? (
                              <div className="flex items-center justify-center w-full h-full bg-muted">
                                <ImageOff className="w-8 h-8 text-muted-foreground" />
                              </div>
                            ) : (
                              <Image
                                src={trip.images[1]}
                                alt="Property image 2"
                                title="Property image 2"
                                fill
                                className="object-cover cursor-pointer hover:scale-105 transition-transform duration-300"
                                onError={() => handleImageError(1)}
                              />
                            )}
                          </div>
                          <div
                            className="flex-1 relative rounded-xl overflow-hidden"
                            onClick={() =>
                              !failedImages.has(2) && openZoomModal(2)
                            }
                          >
                            {failedImages.has(2) ? (
                              <div className="flex items-center justify-center w-full h-full bg-muted">
                                <ImageOff className="w-8 h-8 text-muted-foreground" />
                              </div>
                            ) : (
                              <Image
                                src={trip.images[2]}
                                alt="Property image 3"
                                fill
                                className="object-cover cursor-pointer hover:scale-105 transition-transform duration-300"
                                onError={() => handleImageError(2)}
                              />
                            )}
                            <button
                              type="button"
                              title="See all photos"
                              className="absolute inset-0 bg-black/40 flex items-center justify-center gap-2 text-white font-medium hover:bg-black/50 transition-colors"
                              onClick={(e) => {
                                e.stopPropagation();
                                openZoomModal(0);
                              }}
                            >
                              <Camera className="w-5 h-5" />
                              <span>See all</span>
                            </button>
                          </div>
                        </div>
                      </>
                    )}

                    {/* 4 Images Layout - 1 large left + 1 top right + 2 bottom right */}
                    {trip.images.length === 4 && (
                      <>
                        <div
                          className="w-1/2 relative rounded-xl overflow-hidden"
                          onClick={() =>
                            !failedImages.has(0) && openZoomModal(0)
                          }
                        >
                          {failedImages.has(0) ? (
                            <div className="flex items-center justify-center w-full h-full bg-muted">
                              <ImageOff className="w-8 h-8 text-muted-foreground" />
                            </div>
                          ) : (
                            <Image
                              src={trip.images[0]}
                              alt="Main property image"
                              title="Main property image"
                              fetchPriority="high"
                              fill
                              className="object-cover cursor-pointer hover:scale-105 transition-transform duration-300"
                              onError={() => handleImageError(0)}
                            />
                          )}
                        </div>
                        <div className="w-1/2 flex flex-col gap-2">
                          <div
                            className="flex-1 relative rounded-xl overflow-hidden"
                            onClick={() =>
                              !failedImages.has(1) && openZoomModal(1)
                            }
                          >
                            {failedImages.has(1) ? (
                              <div className="flex items-center justify-center w-full h-full bg-muted">
                                <ImageOff className="w-8 h-8 text-muted-foreground" />
                              </div>
                            ) : (
                              <Image
                                src={trip.images[1]}
                                alt="Property image 2"
                                title="Property image 2"
                                fill
                                className="object-cover cursor-pointer hover:scale-105 transition-transform duration-300"
                                onError={() => handleImageError(1)}
                              />
                            )}
                          </div>
                          <div className="flex-1 flex gap-2">
                            <div
                              className="flex-1 relative rounded-xl overflow-hidden"
                              onClick={() =>
                                !failedImages.has(2) && openZoomModal(2)
                              }
                            >
                              {failedImages.has(2) ? (
                                <div className="flex items-center justify-center w-full h-full bg-muted">
                                  <ImageOff className="w-8 h-8 text-muted-foreground" />
                                </div>
                              ) : (
                                <Image
                                  src={trip.images[2]}
                                  alt="Property image 3"
                                  fill
                                  className="object-cover cursor-pointer hover:scale-105 transition-transform duration-300"
                                  onError={() => handleImageError(2)}
                                />
                              )}
                            </div>
                            <div
                              className="flex-1 relative rounded-xl overflow-hidden"
                              onClick={() =>
                                !failedImages.has(3) && openZoomModal(3)
                              }
                            >
                              {failedImages.has(3) ? (
                                <div className="flex items-center justify-center w-full h-full bg-muted">
                                  <ImageOff className="w-8 h-8 text-muted-foreground" />
                                </div>
                              ) : (
                                <Image
                                  src={trip.images[3]}
                                  alt="Property image 4"
                                  fill
                                  className="object-cover cursor-pointer hover:scale-105 transition-transform duration-300"
                                  onError={() => handleImageError(3)}
                                />
                              )}
                              <button
                                type="button"
                                title="See all photos"
                                className="absolute inset-0 bg-black/40 flex items-center justify-center gap-2 text-white font-medium hover:bg-black/50 transition-colors"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openZoomModal(0);
                                }}
                              >
                                <Camera className="w-5 h-5" />
                                <span>See all</span>
                              </button>
                            </div>
                          </div>
                        </div>
                      </>
                    )}

                    {/* 5+ Images Layout - 1 large left + 2x2 grid right */}
                    {trip.images.length >= 5 && (
                      <>
                        <div
                          className="w-1/2 relative rounded-xl overflow-hidden"
                          onClick={() =>
                            !failedImages.has(0) && openZoomModal(0)
                          }
                        >
                          {failedImages.has(0) ? (
                            <div className="flex items-center justify-center w-full h-full bg-muted">
                              <ImageOff className="w-8 h-8 text-muted-foreground" />
                            </div>
                          ) : (
                            <Image
                              src={trip.images[0]}
                              alt="Main property image"
                              title="Main property image"
                              fetchPriority="high"
                              fill
                              className="object-cover cursor-pointer hover:scale-105 transition-transform duration-300"
                              onError={() => handleImageError(0)}
                            />
                          )}
                        </div>
                        <div className="w-1/2 grid grid-cols-2 grid-rows-2 gap-2">
                          <div
                            className="relative rounded-xl overflow-hidden"
                            onClick={() =>
                              !failedImages.has(1) && openZoomModal(1)
                            }
                          >
                            {failedImages.has(1) ? (
                              <div className="flex items-center justify-center w-full h-full bg-muted">
                                <ImageOff className="w-6 h-6 text-muted-foreground" />
                              </div>
                            ) : (
                              <Image
                                src={trip.images[1]}
                                alt="Gallery image 2"
                                title="Gallery image 2"
                                fill
                                className="object-cover cursor-pointer hover:scale-105 transition-transform duration-300"
                                onError={() => handleImageError(1)}
                              />
                            )}
                          </div>
                          <div
                            className="relative rounded-xl overflow-hidden"
                            onClick={() =>
                              !failedImages.has(2) && openZoomModal(2)
                            }
                          >
                            {failedImages.has(2) ? (
                              <div className="flex items-center justify-center w-full h-full bg-muted">
                                <ImageOff className="w-6 h-6 text-muted-foreground" />
                              </div>
                            ) : (
                              <Image
                                src={trip.images[2]}
                                alt="Gallery image 3"
                                title="Gallery image 3"
                                fill
                                className="object-cover cursor-pointer hover:scale-105 transition-transform duration-300"
                                onError={() => handleImageError(2)}
                              />
                            )}
                          </div>
                          <div
                            className="relative rounded-xl overflow-hidden"
                            onClick={() =>
                              !failedImages.has(3) && openZoomModal(3)
                            }
                          >
                            {failedImages.has(3) ? (
                              <div className="flex items-center justify-center w-full h-full bg-muted">
                                <ImageOff className="w-6 h-6 text-muted-foreground" />
                              </div>
                            ) : (
                              <Image
                                src={trip.images[3]}
                                alt="Gallery image 4"
                                title="Gallery image 4"
                                fill
                                className="object-cover cursor-pointer hover:scale-105 transition-transform duration-300"
                                onError={() => handleImageError(3)}
                              />
                            )}
                          </div>
                          <div
                            className="relative rounded-xl overflow-hidden"
                            onClick={() =>
                              !failedImages.has(4) && openZoomModal(4)
                            }
                          >
                            {failedImages.has(4) ? (
                              <div className="flex items-center justify-center w-full h-full bg-muted">
                                <ImageOff className="w-6 h-6 text-muted-foreground" />
                              </div>
                            ) : (
                              <Image
                                src={trip.images[4]}
                                alt="Gallery image 5"
                                title="Gallery image 5"
                                fill
                                className="object-cover cursor-pointer hover:scale-105 transition-transform duration-300"
                                onError={() => handleImageError(4)}
                              />
                            )}
                            <button
                              type="button"
                              title="See all photos"
                              className="absolute inset-0 bg-black/40 flex items-center justify-center gap-2 text-white font-medium hover:bg-black/50 transition-colors"
                              onClick={(e) => {
                                e.stopPropagation();
                                openZoomModal(0);
                              }}
                            >
                              <Camera className="w-5 h-5" />
                              <span>See all</span>
                            </button>
                          </div>
                        </div>
                      </>
                    )}
                  </div>

                  {/* Mobile Gallery - Dynamic */}
                  <div className="flex-1 w-full h-full flex gap-1 lg:hidden">
                    {trip.images.length === 1 ? (
                      <div
                        className="w-full relative rounded-2xl overflow-hidden"
                        onClick={() => !failedImages.has(0) && openZoomModal(0)}
                      >
                        {failedImages.has(0) ? (
                          <div className="flex items-center justify-center w-full h-full bg-muted">
                            <ImageOff className="w-8 h-8 text-muted-foreground" />
                          </div>
                        ) : (
                          <Image
                            src={trip.images[0]}
                            alt="Property image"
                            fill
                            className="object-cover cursor-pointer"
                            onError={() => handleImageError(0)}
                          />
                        )}
                        <Link
                          title="View"
                          href={`/properties/${slug}/${linkSegment}/photo`}
                          className="absolute bottom-4 right-4 bg-white/90 backdrop-blur-sm px-3 py-1.5 rounded-full flex items-center gap-2 text-black text-sm font-medium"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Camera className="w-4 h-4" />
                          <span>View</span>
                        </Link>
                      </div>
                    ) : trip.images.length === 2 ? (
                      <>
                        <div
                          className="flex-1 relative rounded-l-2xl overflow-hidden"
                          onClick={() =>
                            !failedImages.has(0) && openZoomModal(0)
                          }
                        >
                          {failedImages.has(0) ? (
                            <div className="flex items-center justify-center w-full h-full bg-muted">
                              <ImageOff className="w-8 h-8 text-muted-foreground" />
                            </div>
                          ) : (
                            <Image
                              src={trip.images[0]}
                              alt="Property image 1"
                              fill
                              className="object-cover cursor-pointer"
                              onError={() => handleImageError(0)}
                            />
                          )}
                        </div>
                        <div
                          className="flex-1 relative rounded-r-2xl overflow-hidden"
                          onClick={() =>
                            !failedImages.has(1) && openZoomModal(1)
                          }
                        >
                          {failedImages.has(1) ? (
                            <div className="flex items-center justify-center w-full h-full bg-muted">
                              <ImageOff className="w-8 h-8 text-muted-foreground" />
                            </div>
                          ) : (
                            <Image
                              src={trip.images[1]}
                              alt="Property image 2"
                              title="Property image 2"
                              fill
                              className="object-cover cursor-pointer"
                              onError={() => handleImageError(1)}
                            />
                          )}
                          <button
                            type="button"
                            title="See all photos"
                            className="absolute inset-0 bg-black/40 flex items-center justify-center gap-2 text-white font-medium hover:bg-black/50 transition-colors"
                            onClick={(e) => {
                              e.stopPropagation();
                              openZoomModal(0);
                            }}
                          >
                            <Camera className="w-4 h-4" />
                            <span>See all</span>
                          </button>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="flex-1 grid grid-cols-1 grid-rows-2 gap-1">
                          <div
                            className="flex-1 relative rounded-tl-2xl rounded-bl-2xl overflow-hidden"
                            onClick={() =>
                              !failedImages.has(0) && openZoomModal(0)
                            }
                          >
                            {failedImages.has(0) ? (
                              <div className="flex items-center justify-center w-full h-full bg-muted">
                                <ImageOff className="w-6 h-6 text-muted-foreground" />
                              </div>
                            ) : (
                              <Image
                                src={trip.images[0]}
                                alt="Gallery image 1"
                                title="Gallery image 1"
                                fill
                                className="object-cover cursor-pointer"
                                onError={() => handleImageError(0)}
                              />
                            )}
                          </div>
                          {trip.images[1] && (
                            <div
                              className="flex-1 relative overflow-hidden"
                              onClick={() =>
                                !failedImages.has(1) && openZoomModal(1)
                              }
                            >
                              {failedImages.has(1) ? (
                                <div className="flex items-center justify-center w-full h-full bg-muted">
                                  <ImageOff className="w-6 h-6 text-muted-foreground" />
                                </div>
                              ) : (
                                <Image
                                  src={trip.images[1]}
                                  alt="Gallery image 2"
                                  title="Gallery image 2"
                                  fill
                                  className="object-cover cursor-pointer"
                                  onError={() => handleImageError(1)}
                                />
                              )}
                            </div>
                          )}
                        </div>
                        <div className="flex-1 grid grid-cols-1 grid-rows-2 gap-1">
                          {trip.images[2] && (
                            <div
                              className="flex-1 relative rounded-tr-2xl overflow-hidden"
                              onClick={() =>
                                !failedImages.has(2) && openZoomModal(2)
                              }
                            >
                              {failedImages.has(2) ? (
                                <div className="flex items-center justify-center w-full h-full bg-muted">
                                  <ImageOff className="w-6 h-6 text-muted-foreground" />
                                </div>
                              ) : (
                                <Image
                                  src={trip.images[2]}
                                  alt="Gallery image 3"
                                  title="Gallery image 3"
                                  fill
                                  className="object-cover cursor-pointer"
                                  onError={() => handleImageError(2)}
                                />
                              )}
                            </div>
                          )}
                          <div
                            className="flex-1 relative rounded-br-2xl overflow-hidden"
                            onClick={() => {
                              const idx =
                                trip.images.length > 3
                                  ? 3
                                  : trip.images.length - 1;
                              !failedImages.has(idx) && openZoomModal(idx);
                            }}
                          >
                            {(() => {
                              const idx =
                                trip.images.length > 3
                                  ? 3
                                  : trip.images.length - 1;
                              return failedImages.has(idx) ? (
                                <div className="flex items-center justify-center w-full h-full bg-muted">
                                  <ImageOff className="w-6 h-6 text-muted-foreground" />
                                </div>
                              ) : (
                                <Image
                                  src={trip.images[idx]}
                                  alt="Gallery image 4"
                                  title="Gallery image 4"
                                  fill
                                  className="object-cover cursor-pointer"
                                  onError={() => handleImageError(idx)}
                                />
                              );
                            })()}
                            <button
                              type="button"
                              title="See all photos"
                              className="absolute inset-0 bg-black/40 flex items-center justify-center gap-2 text-white font-medium hover:bg-black/50 transition-colors"
                              onClick={(e) => {
                                e.stopPropagation();
                                openZoomModal(0);
                              }}
                            >
                              <Camera className="w-4 h-4" />
                              <span>See all</span>
                            </button>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </>
              )}
            </div>

            {/* Property Title & Actions */}
            <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4 mt-6">
              <div className="flex flex-col gap-1">
                <h1 className="text-2xl md:text-3xl font-bold text-foreground capitalize">
                  {trip.title}
                </h1>
                <div className="flex items-center gap-2 text-muted-foreground">
                  <MapPin className="w-4 h-4" />
                  <span className="text-sm capitalize">
                    {trip.summary.location}
                  </span>
                </div>
              </div>
              {/* Action Buttons */}
              <div className="flex items-center gap-2">
                {/* Share Button */}
                <button
                  onClick={() => setIsShareDialogOpen(true)}
                  className="flex items-center gap-2 px-3 py-2 rounded-full border border-border hover:border-foreground/50 hover:bg-secondary/50 transition-all"
                >
                  <Share2 className="w-4 h-4" />
                  <span className="text-sm font-medium">Share</span>
                </button>
              </div>
            </div>

            {/* Share Dialog */}
            <Dialog
              open={isShareDialogOpen}
              onOpenChange={setIsShareDialogOpen}
            >
              <DialogContent className="sm:max-w-md rounded-2xl p-0 overflow-hidden gap-0">
                <div className="flex items-center justify-between p-4 border-b">
                  <DialogTitle className="text-lg font-bold">Share</DialogTitle>
                </div>
                <div className="p-4 flex flex-col gap-2">
                  <button
                    onClick={handleCopyLink}
                    className="flex items-center gap-4 p-3 rounded-xl hover:bg-secondary/50 transition-colors w-full text-left group"
                  >
                    <div className="w-10 h-10 rounded-full border border-border flex items-center justify-center bg-background group-hover:border-foreground/20 transition-colors">
                      {isCopied ? (
                        <Check className="w-5 h-5 text-green-600" />
                      ) : (
                        <Copy className="w-5 h-5 text-foreground" />
                      )}
                    </div>
                    <span className="font-medium text-foreground">
                      {isCopied ? "Link copied!" : "Copy link to apartment"}
                    </span>
                  </button>

                  <button
                    onClick={handleEmailApartment}
                    className="flex items-center gap-4 p-3 rounded-xl hover:bg-secondary/50 transition-colors w-full text-left group"
                  >
                    <div className="w-10 h-10 rounded-full border border-border flex items-center justify-center bg-background group-hover:border-foreground/20 transition-colors">
                      <Mail className="w-5 h-5 text-foreground" />
                    </div>
                    <span className="font-medium text-foreground">
                      Email apartment
                    </span>
                  </button>
                </div>
              </DialogContent>
            </Dialog>

            {/* Property Stats - Matching Reference Image */}
            <div className="flex flex-wrap md:flex-nowrap items-stretch mt-6 border border-border rounded-xl bg-background overflow-hidden">
              {/* Bedroom */}
              <div className="flex-1 min-w-[120px] p-4 flex flex-col gap-1">
                <span className="text-sm text-muted-foreground">Bedroom</span>
                <div className="flex items-center gap-2">
                  <Bed className="w-4 h-4 text-muted-foreground" />
                  <span className="font-semibold text-foreground">
                    {trip.summary.bedrooms}
                  </span>
                </div>
              </div>

              {/* Divider */}
              <div className="hidden md:block w-px bg-border"></div>

              {/* Bathroom */}
              <div className="flex-1 min-w-[120px] p-4 flex flex-col gap-1 border-t md:border-t-0 border-border">
                <span className="text-sm text-muted-foreground">Bathroom</span>
                <div className="flex items-center gap-2">
                  <Bath className="w-4 h-4 text-muted-foreground" />
                  <span className="font-semibold text-foreground">
                    {trip.summary.bathrooms}
                  </span>
                </div>
              </div>

              {trip.summary.area && (
                <>
                  {/* Divider */}
                  <div className="hidden md:block w-px bg-border"></div>

                  {/* Area */}
                  <div className="flex-1 min-w-[120px] p-4 flex flex-col gap-1 border-t md:border-t-0 border-border">
                    <span className="text-sm text-muted-foreground">Area</span>
                    <div className="flex items-center gap-2">
                      <Square className="w-4 h-4 text-muted-foreground" />
                      <span className="font-semibold text-foreground">
                        {trip.summary.area}
                      </span>
                    </div>
                  </div>
                </>
              )}

              {/* Divider */}
              <div className="hidden md:block w-px bg-border"></div>

              {/* Parking */}
              {/* <div className="flex-1 min-w-[120px] p-4 flex flex-col gap-1 border-t md:border-t-0 border-border">
                <span className="text-sm text-muted-foreground">Parking</span>
                <div className="flex items-center gap-2">
                  <Car className="w-4 h-4 text-muted-foreground" />
                  <span className="font-semibold text-foreground">{trip.summary.parking || 'Indoor'}</span>
                </div>
              </div> */}

              {/* Type of Room */}
              {trip.type_of_room && (
                <>
                  <div className="hidden md:block w-px bg-border"></div>
                  <div className="flex-1 min-w-[120px] p-4 flex flex-col gap-1 border-t md:border-t-0 border-border">
                    <span className="text-sm text-muted-foreground">
                      Room Type
                    </span>
                    <div className="flex items-center gap-2">
                      <Bed className="w-4 h-4 text-muted-foreground" />
                      <span className="font-semibold text-foreground capitalize">
                        {trip.type_of_room}
                      </span>
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* Video Tour */}
            {trip.property_video && (
              <div className="mt-8 rounded-2xl border border-border bg-background overflow-hidden p-4 md:p-6 shadow-sm">
                <h3 className="text-xl font-bold mb-4">Video Tour</h3>
                <div className="relative aspect-video rounded-xl overflow-hidden bg-black/5">
                  <video
                    controls
                    className="w-full h-full object-contain"
                    src={trip.property_video}
                  />
                </div>
              </div>
            )}

            {/* Mobile Price + Reserve/Visit — inline block (replaces fixed bottom bar) */}
            <div className="lg:hidden flex flex-col mt-6 p-4 rounded-2xl border border-border bg-muted/30 gap-4">
              <div className="flex items-center justify-between">
                <div>
                  <span className="text-xl font-bold text-primary">
                    {trip.pricing.currency} {trip.pricing.from.toLocaleString()}
                  </span>
                  {rentTypeDisplay && (
                    <span className="text-muted-foreground text-sm">
                      {rentTypeDisplay}
                    </span>
                  )}
                </div>
              </div>

              <div className="flex gap-2">
                {!isYearly && (
                  <Button
                    onClick={() => setIsMobileDrawerOpen(true)}
                    className="flex-1 h-11 bg-primary hover:bg-primary/90 text-primary-foreground rounded-full font-semibold shadow-md"
                  >
                    Reserve
                  </Button>
                )}
                <Button
                  onClick={() => {
                    const drawer = document.getElementById(
                      "mobile-schedule-drawer",
                    );
                    if (drawer) drawer.style.display = "block";
                  }}
                  variant={isYearly ? "default" : "outline"}
                  className={
                    isYearly
                      ? "flex-1 h-11 bg-primary hover:bg-primary/90 text-primary-foreground rounded-full font-semibold shadow-md"
                      : "flex-1 h-11 border-2 border-primary text-primary hover:bg-primary/5 rounded-full font-semibold shadow-sm"
                  }
                >
                  Schedule Visit
                </Button>
              </div>
            </div>

            {/* Divider */}
            <div className="w-full h-px my-8">
              <div className="flex-grow h-px bg-gradient-to-r from-transparent via-border to-transparent"></div>
            </div>

            {/* Description */}
            <div className="flex flex-col gap-4">
              <h2 className="text-xl font-semibold">About The Property</h2>

              <div className="relative w-full">
                {trip.descriptionHtml ? (
                  <RichTextContent
                    className="rich-text text-gray-900 leading-relaxed"
                    html={trip.descriptionHtml}
                  />
                ) : (
                  <p className="rich-text text-gray-900 leading-relaxed">
                    {trip.description}
                  </p>
                )}
              </div>
            </div>

            {/* Divider */}
            <div className="w-full h-px my-8">
              <div className="grow h-px bg-linear-to-r from-transparent via-border to-transparent"></div>
            </div>

            {/* Consolidated Property Details Section */}
            <div className="mt-8 bg-background">
              <h2 className="text-2xl font-bold tracking-tight text-foreground mb-8">
                What this home offers
              </h2>

              {(() => {
                // Display amenities straight from the database
                const allSourceAmenities = [
                  ...(trip.amenities || []).map((text) => ({
                    text,
                    type: "database",
                  })),
                ];

                // Deduplicate by text (ignore case)
                const seen = new Set();
                const allAmenities = allSourceAmenities.filter((item) => {
                  const normalizedText = item.text?.toLowerCase().trim();
                  if (!normalizedText || seen.has(normalizedText)) return false;
                  seen.add(normalizedText);
                  return true;
                });

                const displayedAmenities = showAllAmenities
                  ? allAmenities
                  : allAmenities.slice(0, 10);

                if (allAmenities.length === 0)
                  return (
                    <p className="text-muted-foreground italic">
                      No amenities listed for this property.
                    </p>
                  );

                return (
                  <div className="flex flex-col gap-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {displayedAmenities.map((item, index) => (
                        <div
                          key={`${item.type}-${index}`}
                          className="flex items-start gap-4 p-2 rounded-2xl bg-muted/40 border border-border/80 hover:bg-muted/80 hover:-translate-y-1 hover:shadow-md transition-all duration-300 group"
                        >
                          <span className="text-[26px] flex shrink-0 text-primary group-hover:scale-110 transition-transform duration-300">
                            {React.createElement(getAmenityIcon(item.text), {
                              className: "w-6 h-6",
                              strokeWidth: 1.5,
                            })}
                          </span>
                          <div className="flex flex-col pt-0.5">
                            <span className="text-[15px] font-medium text-foreground leading-snug">
                              {item.text}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>

                    {allAmenities.length > 10 && (
                      <div className="mt-2 pt-2 border-t border-border/30">
                        <Button
                          onClick={() => setShowAllAmenities(!showAllAmenities)}
                          variant="outline"
                          className="h-12 px-8 rounded-full border-2 hover:bg-primary hover:text-primary-foreground hover:border-primary transition-all duration-300 font-semibold"
                        >
                          {showAllAmenities
                            ? "Show less"
                            : `Show all ${allAmenities.length} amenities`}
                        </Button>
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>

            {/* Divider */}
            <div className="w-full h-px my-10">
              <div className="flex-grow h-px bg-gradient-to-r from-transparent via-border to-transparent"></div>
            </div>

            {/* Booking & stay rules Section */}
            <div className=" bg-card rounded-2xl border border-border/80 shadow-sm overflow-hidden p-6 md:p-8">
              <h2 className="text-2xl font-bold tracking-tight text-foreground mb-8">
                Booking & stay rules
              </h2>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16">
                <div className="flex flex-col">
                  <p className="text-base font-semibold mb-5 text-foreground flex items-center gap-2.5">
                    <div className="w-1.5 h-1.5 rounded-full bg-primary"></div>
                    Cancellation policy
                  </p>
                  <ul className="space-y-3 text-[14.5px] text-muted-foreground">
                    {cancellationRules.map((rule, index) => (
                      <li
                        key={`cancellation-${index}`}
                        className="flex items-start gap-2.5 leading-relaxed"
                      >
                        <span className="mt-2 h-1.5 w-1.5 rounded-full bg-primary/70 shrink-0" />
                        <span>{rule}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="flex flex-col">
                  <p className="text-base font-semibold mb-5 text-foreground flex items-center gap-2.5">
                    <div className="w-1.5 h-1.5 rounded-full bg-primary"></div>
                    Home rules
                  </p>
                  <ul className="space-y-3 text-[14.5px] text-muted-foreground">
                    {stayRules.map((rule, index) => (
                      <li
                        key={`stay-${index}`}
                        className="flex items-start gap-2.5 leading-relaxed"
                      >
                        <span className="mt-2 h-1.5 w-1.5 rounded-full bg-primary/70 shrink-0" />
                        <span>{rule}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              {otherRules.length > 0 && (
                <div className="mt-8">
                  <h3 className="text-base font-semibold mb-5 text-foreground flex items-center gap-2.5">
                    <div className="w-1.5 h-1.5 rounded-full bg-primary"></div>
                    Other rules
                  </h3>
                  <ul className="space-y-3 text-[14.5px] text-muted-foreground">
                    {otherRules.map((rule, index) => (
                      <li
                        key={`other-${index}`}
                        className="flex items-start gap-2.5 leading-relaxed"
                      >
                        <span className="mt-2 h-1.5 w-1.5 rounded-full bg-primary/70 shrink-0" />
                        <span>{rule}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            {/* Divider */}
            <div className="w-full h-px my-10">
              <div className="flex-grow h-px bg-gradient-to-r from-transparent via-border to-transparent"></div>
            </div>

            {/* FAQs */}
            {displayedFaqs.length > 0 && (
              <div className="flex flex-col gap-6">
                <h2 className="text-xl font-semibold">
                  Frequently Asked Questions
                </h2>
                <div className="w-full flex flex-col gap-0 border border-border/50 rounded-xl overflow-hidden bg-muted/5">
                  {displayedFaqs.map((faq, index) => (
                    <div
                      key={faq.id}
                      className={
                        index < displayedFaqs.length - 1
                          ? "border-b border-border/50"
                          : ""
                      }
                    >
                      <button
                        onClick={() => toggleFAQ(faq.id)}
                        className="w-full flex flex-1 items-center justify-between py-4 px-4 transition-all text-left hover:bg-muted/50"
                      >
                        <h3 className="font-semibold text-left pr-4">
                          {faq.question}
                        </h3>
                        <ChevronDown
                          className={cn(
                            "h-4 w-4 shrink-0 transition-transform duration-200",
                            expandedFAQ === faq.id ? "rotate-180" : "",
                          )}
                        />
                      </button>

                      <div
                        className={`overflow-hidden transition-all duration-300 ease-in-out ${expandedFAQ === faq.id ? "max-h-[1000px] opacity-100" : "max-h-0 opacity-0"}`}
                      >
                        <div className="px-4 pb-4">
                          <p className="text-muted-foreground leading-relaxed whitespace-pre-wrap">
                            {faq.answer}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Divider */}
            <div className="w-full h-px my-8">
              <div className="flex-grow h-px bg-gradient-to-r from-transparent via-border to-transparent"></div>
            </div>

            {/* Properties available in the same area */}
            {similarProperties && similarProperties.length > 0 && (
              <div className="flex flex-col gap-6">
                <h2 className="text-xl font-semibold">
                  Properties available in the {trip.summary.location}
                </h2>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {similarProperties.map((property: any, index: number) => {
                    const rawImageUrl =
                      property.cover_image?.[0] ||
                      property.images?.[0] ||
                      trip.images[0];
                    const imageUrl = resolvePropertyImageUrl(rawImageUrl, {
                      propertyId:
                        typeof property.id === "string" ? property.id : null,
                      imageIndex: 0,
                    });
                    return (
                      <Link
                        title=""
                        href={getPropertyHref(property)}
                        key={property.id || index}
                        className="relative aspect-[4/3] rounded-xl overflow-hidden group cursor-pointer block"
                      >
                        <Image
                          src={imageUrl}
                          alt={
                            property.title || `Related property ${index + 1}`
                          }
                          fill
                          className="object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>

                        <div className="absolute bottom-0 left-0 right-0 p-3 text-white">
                          <h3 className="font-semibold text-sm line-clamp-1">
                            {property.title}
                          </h3>
                          <div className="flex items-center justify-between mt-1">
                            <span className="text-xs opacity-90">
                              {property.location_data?.short_name ||
                                property.location}
                            </span>
                            <span className="text-xs font-bold">
                              {trip.pricing.currency}{" "}
                              {property.price?.toLocaleString()}
                              {(() => {
                                const relatedRentType = String(
                                  property.rent_type || "",
                                ).toLowerCase();
                                if (relatedRentType === "annual") return " /yr";
                                if (relatedRentType === "monthly")
                                  return " /mo";
                                return "";
                              })()}
                            </span>
                          </div>
                        </div>
                      </Link>
                    );
                  })}
                </div>
              </div>
            )}

            {/* If no similar properties, show nothing or fallback? Kept hidden if empty */}
          </div>

          {/* Right Sidebar - Booking Card (Sticky) - Compact */}
          <div className="hidden lg:block w-[340px] shrink-0">
            <div className="sticky top-24 z-10 transition-all duration-300">
              <div className="flex rounded-2xl flex-col border bg-background border-border shadow-xl shadow-black/5 max-h-[calc(100vh-120px)] overflow-y-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
                <div className="flex flex-col p-4">
                  {trip.available_from &&
                    new Date(trip.available_from) >
                      new Date(new Date().setHours(0, 0, 0, 0)) &&
                    !rentalData && (
                      <div className="mb-3 px-3 py-2 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800/40 rounded-xl text-xs text-amber-800 dark:text-amber-200 text-center">
                        Available from{" "}
                        <strong>
                          {new Date(trip.available_from).toLocaleDateString(
                            "en-US",
                            { day: "numeric", month: "long", year: "numeric" },
                          )}
                        </strong>
                      </div>
                    )}

                  {/* Property Name & Tags */}
                  {/* <div className="mb-2">
                    <h3 className="text-lg font-bold text-foreground line-clamp-2 leading-tight">{trip.title}</h3>
                    <div className="flex items-center gap-1.5 mt-1 text-muted-foreground">
                      <MapPin className="w-3.5 h-3.5" />
                      <span className="text-xs">{trip.summary.location}</span>
                    </div>
                  </div> */}

                  {/* Rental Status Alert — show when property has any current/upcoming bookings */}
                  {hasUpcomingBookings && nextAvailableFromDate && (
                    <div className="mb-2 group relative overflow-hidden rounded-xl bg-gradient-to-br from-amber-50 to-orange-50/50 p-4 border border-amber-100 dark:from-amber-950/20 dark:to-orange-950/20 dark:border-amber-900/30">
                      <div className="flex items-center gap-3">
                        <div className="relative flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white shadow-sm ring-1 ring-amber-100 dark:bg-amber-950 dark:ring-amber-900/50">
                          <CalendarIcon className="h-5 w-5 text-amber-600 dark:text-amber-400" />
                          <div className="absolute -bottom-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-green-500 ring-2 ring-white dark:ring-amber-950">
                            <Check className="h-2.5 w-2.5 text-white" />
                          </div>
                        </div>
                        <div className="flex flex-col gap-0.5">
                          {isWaitlistOnlyAvailability ? (
                            <>
                              <span className="font-bold text-sm text-amber-900 dark:text-amber-100">
                                Booked - Join The Waitlist
                              </span>
                              <span className="text-[11px] text-amber-800/80 dark:text-amber-100/80">
                                Online booking opens when availability is within{" "}
                                {AVAILABILITY_SOON_DAYS} days.
                              </span>
                            </>
                          ) : (
                            <>
                              <span className="text-[10px] font-bold uppercase tracking-wider text-amber-700/70 dark:text-amber-400/70">
                                Available From
                              </span>
                              <span className="font-bold text-sm text-amber-900 dark:text-amber-100">
                                {nextAvailableFromDate.toLocaleDateString(
                                  "en-US",
                                  {
                                    day: "numeric",
                                    month: "long",
                                    year: "numeric",
                                  },
                                )}
                              </span>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Price */}
                  <div className="mb-2">
                    <span className="text-xl font-bold text-primary">
                      {trip.pricing.currency}{" "}
                      {trip.pricing.from.toLocaleString()}
                    </span>
                    {rentTypeDisplay && (
                      <span className="text-muted-foreground text-sm ml-1">
                        {rentTypeDisplay}
                      </span>
                    )}
                  </div>

                  {/* Check In / Check Out (Unified Bar Design) */}
                  <div className="flex w-full border border-border rounded-xl mb-4 overflow-hidden shadow-sm ring-offset-background focus-within:ring-2 focus-within:ring-primary/20 transition-all">
                    <Popover>
                      <PopoverTrigger asChild>
                        <button className="flex-1 p-3 text-left hover:bg-secondary/50 transition-colors focus:outline-none focus:bg-secondary/50 group/checkin relative">
                          <label className="text-[10px] text-muted-foreground font-bold uppercase tracking-wider block mb-0.5 cursor-pointer group-hover/checkin:text-primary transition-colors">
                            Check In
                          </label>
                          <div
                            className={cn(
                              "font-semibold text-sm flex items-center",
                              !checkInDate
                                ? "text-muted-foreground font-medium"
                                : "text-foreground",
                            )}
                          >
                            <CalendarIcon
                              className={cn(
                                "mr-2 h-4 w-4 shrink-0 transition-opacity",
                                checkInDate
                                  ? "opacity-100 text-primary"
                                  : "opacity-40",
                              )}
                            />
                            {checkInDate
                              ? format(checkInDate, "dd MMM yyyy")
                              : "Add date"}
                          </div>
                        </button>
                      </PopoverTrigger>
                      <PopoverContent
                        className="w-auto p-0 rounded-2xl overflow-hidden shadow-lg border-border"
                        align="start"
                      >
                        <Calendar
                          mode="single"
                          selected={checkInDate}
                          onSelect={handleCheckInSelect}
                          disabled={(date) => {
                            const today = new Date();
                            today.setHours(0, 0, 0, 0);
                            if (date < today) return true;
                            return isDateBooked(date);
                          }}
                        />
                      </PopoverContent>
                    </Popover>

                    <div className="w-[1px] bg-border my-2.5"></div>

                    <Popover>
                      <PopoverTrigger asChild>
                        <button className="flex-1 p-3 text-left hover:bg-secondary/50 transition-colors focus:outline-none focus:bg-secondary/50 group/checkout relative">
                          <label className="text-[10px] text-muted-foreground font-bold uppercase tracking-wider block mb-0.5 cursor-pointer group-hover/checkout:text-primary transition-colors">
                            Check Out
                          </label>
                          <div
                            className={cn(
                              "font-semibold text-sm flex items-center",
                              !checkOutDate
                                ? "text-muted-foreground font-medium"
                                : "text-foreground",
                            )}
                          >
                            <CalendarIcon
                              className={cn(
                                "mr-2 h-4 w-4 shrink-0 transition-opacity",
                                checkOutDate
                                  ? "opacity-100 text-primary"
                                  : "opacity-40",
                              )}
                            />
                            {checkOutDate
                              ? format(checkOutDate, "dd MMM yyyy")
                              : "Add date"}
                          </div>
                        </button>
                      </PopoverTrigger>
                      <PopoverContent
                        className="w-auto p-0 rounded-2xl overflow-hidden shadow-lg border-border"
                        align="end"
                      >
                        <Calendar
                          mode="single"
                          selected={checkOutDate}
                          onSelect={setCheckOutDate}
                          disabled={(date) => {
                            const minDate = checkInDate
                              ? addDays(checkInDate, MIN_PLATFORM_STAY_DAYS)
                              : minimumDate
                                ? addDays(minimumDate, MIN_PLATFORM_STAY_DAYS)
                                : addDays(new Date(), MIN_PLATFORM_STAY_DAYS);
                            if (date < minDate) return true;
                            return isDateBooked(date);
                          }}
                        />
                      </PopoverContent>
                    </Popover>
                  </div>

                  {/* Updated Sidebar Pricing Section - "You won't be charged yet" */}
                  <div className="mb-1">
                    {/* Monthly Breakdown Summary */}
                    <div className="space-y-1 mb-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground underline decoration-dotted underline-offset-2">
                          Rent ({nights} night{nights !== 1 ? "s" : ""})
                        </span>
                        <span>
                          {trip.pricing.currency} {rentAmount.toLocaleString()}
                        </span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground underline decoration-dotted underline-offset-2">
                          VAT ({vatRate}%)
                        </span>
                        <span>
                          {trip.pricing.currency} {vatAmount.toLocaleString()}
                        </span>
                      </div>
                      <div className="flex justify-between text-base font-bold text-foreground pt-1">
                        <span>Stay charges</span>
                        <span>
                          {trip.pricing.currency} {rentTotal.toLocaleString()}
                        </span>
                      </div>
                    </div>

                    {/* View Price Details Button */}
                    <div className="relative flex items-center justify-center mb-2">
                      <div className="absolute inset-0 flex items-center">
                        <div className="w-full border-t border-border"></div>
                      </div>
                      <button
                        onClick={() => setIsPriceDetailsOpen(true)}
                        className="relative bg-background px-4 text-sm font-medium text-primary hover:text-primary/80 border border-border rounded-full py-1 shadow-sm hover:shadow-md transition-all z-10"
                      >
                        View price details
                      </button>
                    </div>

                    {/* Total */}
                    <div className="flex justify-between items-baseline mb-2">
                      <span className="text-xl font-bold">Total</span>
                      <span className="text-xl font-bold">
                        {trip.pricing.currency} {grandTotal.toLocaleString()}
                      </span>
                    </div>

                    {/* Action Buttons */}
                    <div className="space-y-3">
                      {/* Book Now Button — show conflict warning whenever dates overlap with any booking */}
                      {showWaitlistForm ? (
                        <div className="bg-orange-50 text-orange-700 rounded-xl p-3 text-sm border border-orange-200">
                          <strong>Booked - Join Waitlist</strong>
                          <br />
                          This property is not available for online booking for
                          dates beyond {AVAILABILITY_SOON_DAYS} days.
                          <Button
                            onClick={openWaitlistDialog}
                            className="w-full mt-3 h-10 rounded-lg bg-primary hover:bg-primary/90 text-primary-foreground font-semibold"
                          >
                            Join Waitlist
                          </Button>
                        </div>
                      ) : conflictingBooking ? (
                        <div className="bg-red-50 text-red-600 rounded-xl p-3 text-sm border border-red-200">
                          <strong>Already Rented!</strong>
                          <br />
                          Property is properly booked until{" "}
                          <strong>
                            {nextAvailableFromDate
                              ? format(
                                  subDays(nextAvailableFromDate, 1),
                                  "MMM dd, yyyy",
                                )
                              : format(
                                  new Date(conflictingBooking.end_date),
                                  "MMM dd",
                                )}
                          </strong>
                          .
                          <Button
                            onClick={openWaitlistDialog}
                            className="w-full mt-3 h-10 rounded-lg bg-primary hover:bg-primary/90 text-primary-foreground font-semibold"
                          >
                            Join Waitlist
                          </Button>
                        </div>
                      ) : isYearly ? (
                        <div className="bg-blue-50 text-blue-700 rounded-xl p-3 text-sm border border-blue-200">
                          <strong>Yearly plan</strong>
                          <br />
                          Annual leases are arranged in person — schedule a
                          visit below and our team will finalise the details
                          with you.
                        </div>
                      ) : isShortStaySelection ? (
                        <div className="bg-blue-50 text-blue-700 rounded-xl p-3 text-sm border border-blue-200">
                          <strong>
                            Minimum stay is {MIN_PLATFORM_STAY_DAYS} nights.
                          </strong>
                          <br />
                          For shorter stays, submit an enquiry and our team will
                          get back to you.
                          <Button
                            onClick={openShortStayDialog}
                            className="w-full mt-3 h-10 rounded-lg bg-primary hover:bg-primary/90 text-primary-foreground font-semibold"
                          >
                            Send Short Stay Enquiry
                          </Button>
                        </div>
                      ) : (
                        <Button
                          onClick={() => {
                            if (isFutureAvailable) {
                              openWaitlistDialog();
                              return;
                            }
                            setBookingData({
                              checkInDate: checkInDate
                                ?.toISOString()
                                .split("T")[0],
                              checkOutDate: checkOutDate
                                ?.toISOString()
                                .split("T")[0],
                              guests,
                              cancellationPolicy: selectedCancellation,
                              totalAmount: grandTotal,
                            });
                            setIsPaymentDialogOpen(true);
                          }}
                          className="w-full h-12 bg-primary hover:bg-primary/90 text-primary-foreground rounded-xl font-bold text-lg shadow-lg hover:shadow-xl transition-all duration-300"
                        >
                          {isFutureAvailable ? "Join the Waitlist" : "Book Now"}
                        </Button>
                      )}
                    </div>
                  </div>
                </div>

                {/* Bottom Info */}
                {/* <div className="flex flex-col gap-2 p-3 bg-secondary/30 border-t border-border">
                <div className="flex items-start gap-2">
                  <Clock className="w-4 h-4 text-muted-foreground mt-0.5 shrink-0" />
                  <p className="text-xs text-muted-foreground">
                    <span className="font-medium text-foreground">Only 6 hours left</span>, bookings close soon.
                  </p>
                </div>
                <button className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors">
                  <Flag className="w-3.5 h-3.5" />
                  Report this listing
                </button>
              </div> */}

                {(!isFutureAvailable || isYearly) && !showWaitlistForm && (
                  <>
                    {/* ── OR Divider + Schedule Visit (yearly plans always show this) ── */}
                    <div className="border-0">
                      {!isYearly && (
                        <div className="relative flex items-center justify-center py-0 px-4">
                          <div className="absolute inset-x-4 top-1/2 h-px bg-border" />
                          <span className="relative bg-background px-3 text-xs text-muted-foreground font-medium">
                            or
                          </span>
                        </div>
                      )}
                      <ScheduleVisitWidget
                        propertyId={id}
                        propertyTitle={trip.title}
                      />
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Fixed bottom bar removed — Reserve button is now inline after property stats (mobile) */}

          {/* Mobile Booking Drawer Overlay */}
          {isMobileDrawerOpen && (
            <div className="fixed inset-0 z-[60] lg:hidden">
              {/* Backdrop */}
              <div
                className="absolute inset-0 bg-black/60 backdrop-blur-sm transition-opacity"
                onClick={() => setIsMobileDrawerOpen(false)}
              ></div>

              {/* Drawer Content */}
              <div className="absolute bottom-0 left-0 right-0 bg-background rounded-t-3xl shadow-2xl overflow-hidden flex flex-col max-h-[85vh] animate-in slide-in-from-bottom duration-300">
                {/* Handle */}
                <div
                  className="w-full flex items-center justify-center pt-4 pb-2 cursor-pointer"
                  onClick={() => setIsMobileDrawerOpen(false)}
                >
                  <div className="w-16 h-1.5 bg-muted rounded-full"></div>
                </div>

                {/* Scrollable Form Content */}
                <div className="flex-1 overflow-y-auto px-6 pt-2 pb-6">
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="text-xl font-bold">Booking Details</h3>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 rounded-full bg-secondary/50"
                      onClick={() => setIsMobileDrawerOpen(false)}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>

                  {/* Rental Status Alert - Mobile — show when property has any current/upcoming bookings */}
                  {hasUpcomingBookings && nextAvailableFromDate && (
                    <div className="mb-5 group relative overflow-hidden rounded-xl bg-gradient-to-br from-amber-50 to-orange-50/50 p-4 border border-amber-100 dark:from-amber-950/20 dark:to-orange-950/20 dark:border-amber-900/30">
                      <div className="flex items-center gap-3">
                        <div className="relative flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white shadow-sm ring-1 ring-amber-100 dark:bg-amber-950 dark:ring-amber-900/50">
                          <CalendarIcon className="h-5 w-5 text-amber-600 dark:text-amber-400" />
                          <div className="absolute -bottom-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-green-500 ring-2 ring-white dark:ring-amber-950">
                            <Check className="h-2.5 w-2.5 text-white" />
                          </div>
                        </div>
                        <div className="flex flex-col gap-0.5">
                          {isWaitlistOnlyAvailability ? (
                            <>
                              <span className="font-bold text-sm text-amber-900 dark:text-amber-100">
                                Booked - Join The Waitlist
                              </span>
                              <span className="text-[11px] text-amber-800/80 dark:text-amber-100/80">
                                Online booking opens when availability is within{" "}
                                {AVAILABILITY_SOON_DAYS} days.
                              </span>
                            </>
                          ) : (
                            <>
                              <span className="text-[10px] font-bold uppercase tracking-wider text-amber-700/70 dark:text-amber-400/70">
                                Available From
                              </span>
                              <span className="font-bold text-sm text-amber-900 dark:text-amber-100">
                                {nextAvailableFromDate.toLocaleDateString(
                                  "en-US",
                                  {
                                    day: "numeric",
                                    month: "long",
                                    year: "numeric",
                                  },
                                )}
                              </span>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Price */}
                  <div className="mb-6">
                    <span className="text-3xl font-bold text-primary">
                      {trip.pricing.currency}{" "}
                      {trip.pricing.from.toLocaleString()}
                    </span>
                    {rentTypeDisplay && (
                      <span className="text-muted-foreground ml-2 text-lg">
                        {rentTypeDisplay}
                      </span>
                    )}
                  </div>

                  {/* Check In / Check Out */}
                  <div className="flex w-full border border-border rounded-xl mb-5 overflow-hidden shadow-sm ring-offset-background focus-within:ring-2 focus-within:ring-primary/20 transition-all">
                    <Popover>
                      <PopoverTrigger asChild>
                        <button className="flex-1 p-3.5 text-left hover:bg-secondary/50 transition-colors focus:outline-none focus:bg-secondary/50 group/checkin relative">
                          <label className="text-[10px] text-muted-foreground font-bold uppercase tracking-wider block mb-1 cursor-pointer group-hover/checkin:text-primary transition-colors">
                            Check In
                          </label>
                          <div
                            className={cn(
                              "font-semibold text-sm flex items-center",
                              !checkInDate
                                ? "text-muted-foreground font-medium"
                                : "text-foreground",
                            )}
                          >
                            <CalendarIcon
                              className={cn(
                                "mr-2 h-4 w-4 shrink-0 transition-opacity",
                                checkInDate
                                  ? "opacity-100 text-primary"
                                  : "opacity-40",
                              )}
                            />
                            {checkInDate
                              ? format(checkInDate, "dd MMM yyyy")
                              : "Add date"}
                          </div>
                        </button>
                      </PopoverTrigger>
                      <PopoverContent
                        className="w-auto p-0 z-[70] rounded-2xl overflow-hidden shadow-lg border-border"
                        align="start"
                      >
                        <Calendar
                          mode="single"
                          selected={checkInDate}
                          onSelect={handleCheckInSelect}
                          disabled={(date) => {
                            const today = new Date();
                            today.setHours(0, 0, 0, 0);
                            if (date < today) return true;
                            return isDateBooked(date);
                          }}
                        />
                      </PopoverContent>
                    </Popover>

                    <div className="w-[1px] bg-border my-2.5"></div>

                    <Popover>
                      <PopoverTrigger asChild>
                        <button className="flex-1 p-3.5 text-left hover:bg-secondary/50 transition-colors focus:outline-none focus:bg-secondary/50 group/checkout relative">
                          <label className="text-[10px] text-muted-foreground font-bold uppercase tracking-wider block mb-1 cursor-pointer group-hover/checkout:text-primary transition-colors">
                            Check Out
                          </label>
                          <div
                            className={cn(
                              "font-semibold text-sm flex items-center",
                              !checkOutDate
                                ? "text-muted-foreground font-medium"
                                : "text-foreground",
                            )}
                          >
                            <CalendarIcon
                              className={cn(
                                "mr-2 h-4 w-4 shrink-0 transition-opacity",
                                checkOutDate
                                  ? "opacity-100 text-primary"
                                  : "opacity-40",
                              )}
                            />
                            {checkOutDate
                              ? format(checkOutDate, "dd MMM yyyy")
                              : "Add date"}
                          </div>
                        </button>
                      </PopoverTrigger>
                      <PopoverContent
                        className="w-auto p-0 z-[70] rounded-2xl overflow-hidden shadow-lg border-border"
                        align="end"
                      >
                        <Calendar
                          mode="single"
                          selected={checkOutDate}
                          onSelect={setCheckOutDate}
                          disabled={(date) => {
                            const minDate = checkInDate
                              ? addDays(checkInDate, MIN_PLATFORM_STAY_DAYS)
                              : minimumDate
                                ? addDays(minimumDate, MIN_PLATFORM_STAY_DAYS)
                                : addDays(new Date(), MIN_PLATFORM_STAY_DAYS);
                            if (date < minDate) return true;
                            return isDateBooked(date);
                          }}
                        />
                      </PopoverContent>
                    </Popover>
                  </div>

                  {/* Cancellation Policies */}
                  <div className="mb-6">
                    <h4 className="text-sm font-semibold mb-3">
                      Cancellation Policies
                    </h4>

                    {/* Non-Refundable */}
                    <div
                      onClick={() => setSelectedCancellation("non-refundable")}
                      className={`border rounded-xl p-3 mb-2 cursor-pointer transition-all ${
                        selectedCancellation === "non-refundable"
                          ? "border-primary bg-primary/5"
                          : "border-border"
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div
                            className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                              selectedCancellation === "non-refundable"
                                ? "border-primary bg-primary"
                                : "border-border"
                            }`}
                          >
                            {selectedCancellation === "non-refundable" && (
                              <Check className="w-2.5 h-2.5 text-white" />
                            )}
                          </div>
                          <span className="font-medium text-sm">
                            Non-Refundable
                          </span>
                        </div>
                        <span className="text-sm font-semibold">
                          {trip.pricing.currency} {grandTotal.toLocaleString()}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Price breakdown (mirrors the desktop "Price details" dialog) */}
                  <div className="mb-2">
                    <h4 className="text-sm font-semibold mb-3">
                      Price details
                    </h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">
                          Rent ({nights} night{nights !== 1 ? "s" : ""})
                        </span>
                        <span>
                          {trip.pricing.currency} {rentAmount.toLocaleString()}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">
                          VAT ({vatRate}%)
                        </span>
                        <span>
                          {trip.pricing.currency} {vatAmount.toLocaleString()}
                        </span>
                      </div>
                      <div className="flex justify-between font-semibold text-foreground">
                        <span>Stay charges</span>
                        <span>
                          {trip.pricing.currency} {rentTotal.toLocaleString()}
                        </span>
                      </div>
                      {(trip.pricing.fees?.bookingFee || 0) > 0 && (
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">
                            Booking fee
                          </span>
                          <span>
                            {trip.pricing.currency}{" "}
                            {(
                              trip.pricing.fees?.bookingFee || 0
                            ).toLocaleString()}
                          </span>
                        </div>
                      )}
                      {(trip.pricing.fees?.exitCleaningFee || 0) > 0 && (
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">
                            Exit cleaning &amp; restocking fee
                          </span>
                          <span>
                            {trip.pricing.currency}{" "}
                            {(
                              trip.pricing.fees?.exitCleaningFee || 0
                            ).toLocaleString()}
                          </span>
                        </div>
                      )}
                      {(trip.pricing.fees?.tourismFee || 0) > 0 && (
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">
                            Tourism fee
                          </span>
                          <span>
                            {trip.pricing.currency}{" "}
                            {(
                              trip.pricing.fees?.tourismFee || 0
                            ).toLocaleString()}
                          </span>
                        </div>
                      )}
                      {securityDeposit > 0 && (
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">
                            Refundable security deposit
                          </span>
                          <span>
                            {trip.pricing.currency}{" "}
                            {securityDeposit.toLocaleString()}
                          </span>
                        </div>
                      )}
                      <div className="h-px bg-border my-2"></div>
                      <div className="flex justify-between text-base font-bold">
                        <span>Total</span>
                        <span className="text-primary">
                          {trip.pricing.currency} {grandTotal.toLocaleString()}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Footer Actions */}
                <div className="p-4 border-t border-border bg-background pb-8 safe-area-bottom">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-muted-foreground">
                      Total Estimate
                    </span>
                    <span className="text-xl font-bold">
                      {trip.pricing.currency} {grandTotal.toLocaleString()}
                    </span>
                  </div>

                  <div className="flex flex-col gap-3">
                    {/* Book Now Button — show conflict warning whenever dates overlap with any booking */}
                    {showWaitlistForm ? (
                      <div className="bg-orange-50 text-orange-700 rounded-xl p-3 text-sm border border-orange-200">
                        <strong>Booked - Join Waitlist</strong>
                        <br />
                        This property is not available for online booking for
                        dates beyond {AVAILABILITY_SOON_DAYS} days.
                        <Button
                          onClick={() => {
                            setIsMobileDrawerOpen(false);
                            setTimeout(() => openWaitlistDialog(), 200);
                          }}
                          className="w-full mt-3 h-10 rounded-lg bg-primary hover:bg-primary/90 text-primary-foreground font-semibold"
                        >
                          Join Waitlist
                        </Button>
                      </div>
                    ) : conflictingBooking ? (
                      <div className="bg-red-50 text-red-600 rounded-xl p-3 text-sm border border-red-200">
                        <strong>Already Rented!</strong>
                        <br />
                        Property is properly booked until{" "}
                        <strong>
                          {nextAvailableFromDate
                            ? format(
                                subDays(nextAvailableFromDate, 1),
                                "MMM dd, yyyy",
                              )
                            : format(
                                new Date(conflictingBooking.end_date),
                                "MMM dd",
                              )}
                        </strong>
                        .
                        <Button
                          onClick={() => {
                            setIsMobileDrawerOpen(false);
                            setTimeout(() => openWaitlistDialog(), 200);
                          }}
                          className="w-full mt-3 h-10 rounded-lg bg-primary hover:bg-primary/90 text-primary-foreground font-semibold"
                        >
                          Join Waitlist
                        </Button>
                      </div>
                    ) : isShortStaySelection ? (
                      <div className="bg-blue-50 text-blue-700 rounded-xl p-3 text-sm border border-blue-200">
                        <strong>
                          Minimum stay is {MIN_PLATFORM_STAY_DAYS} nights.
                        </strong>
                        <br />
                        For shorter stays, submit an enquiry and our team will
                        get back to you.
                        <Button
                          onClick={() => {
                            setIsMobileDrawerOpen(false);
                            setTimeout(() => openShortStayDialog(), 200);
                          }}
                          className="w-full mt-3 h-10 rounded-lg bg-primary hover:bg-primary/90 text-primary-foreground font-semibold"
                        >
                          Send Short Stay Enquiry
                        </Button>
                      </div>
                    ) : (
                      <Button
                        onClick={() => {
                          if (isFutureAvailable) {
                            setIsMobileDrawerOpen(false);
                            setTimeout(() => openWaitlistDialog(), 200);
                            return;
                          }
                          setBookingData({
                            checkInDate: checkInDate
                              ?.toISOString()
                              .split("T")[0],
                            checkOutDate: checkOutDate
                              ?.toISOString()
                              .split("T")[0],
                            guests,
                            cancellationPolicy: selectedCancellation,
                            totalAmount: grandTotal,
                          });
                          setIsMobileDrawerOpen(false);
                          setTimeout(() => setIsPaymentDialogOpen(true), 300);
                        }}
                        className="w-full h-12 text-base bg-primary hover:bg-primary/90 text-primary-foreground rounded-full font-bold shadow-md"
                      >
                        {isFutureAvailable ? "Join the Waitlist" : "Book Now"}
                      </Button>
                    )}
                    {!isFutureAvailable && !showWaitlistForm && (
                      <Button
                        onClick={() => {
                          setIsMobileDrawerOpen(false);
                          setTimeout(() => {
                            const drawer = document.getElementById(
                              "mobile-schedule-drawer",
                            );
                            if (drawer) drawer.style.display = "block";
                          }, 300);
                        }}
                        variant="outline"
                        className="w-full h-12 text-base rounded-full font-semibold border-2"
                      >
                        Schedule Visit
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Mobile Schedule Drawer Overlay */}
          <div
            id="mobile-schedule-drawer"
            className="hidden fixed inset-0 z-[60] lg:hidden"
          >
            {/* Backdrop */}
            <div
              className="absolute inset-0 bg-black/60 backdrop-blur-sm transition-opacity"
              onClick={(e) => {
                const drawer = document.getElementById(
                  "mobile-schedule-drawer",
                );
                if (drawer) drawer.style.display = "none";
              }}
            ></div>

            {/* Drawer Content */}
            <div className="absolute bottom-0 left-0 right-0 bg-background rounded-t-3xl shadow-2xl overflow-hidden flex flex-col max-h-[85vh] animate-in slide-in-from-bottom duration-300">
              {/* Handle */}
              <div
                className="w-full flex items-center justify-center pt-4 pb-2 cursor-pointer"
                onClick={() => {
                  const drawer = document.getElementById(
                    "mobile-schedule-drawer",
                  );
                  if (drawer) drawer.style.display = "none";
                }}
              >
                <div className="w-16 h-1.5 bg-muted rounded-full"></div>
              </div>

              {/* Header */}
              <div className="px-6 flex items-center justify-between mb-2">
                <h3 className="text-xl font-bold">Schedule a Free Visit</h3>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 rounded-full bg-secondary/50"
                  onClick={() => {
                    const drawer = document.getElementById(
                      "mobile-schedule-drawer",
                    );
                    if (drawer) drawer.style.display = "none";
                  }}
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>

              {/* Scrollable Widget Content */}
              <div className="flex-1 overflow-y-auto px-2 pb-6">
                <ScheduleVisitWidget
                  propertyId={id}
                  propertyTitle={trip.title}
                />
              </div>
            </div>
          </div>
        </div>
        <EnquiryDialog
          isOpen={isEnquiryOpen}
          onClose={() => setIsEnquiryOpen(false)}
          propertyId={id}
          propertyTitle={trip.title}
          propertyImage={trip.images[0]}
          propertyLocation={trip.summary.location}
          enquiryType={
            enquiryFlow === "waitlist" ? "property_waitlist" : "tenant_enquiry"
          }
          dialogTitle={
            enquiryFlow === "waitlist" ? "Join Waitlist" : "Short Stay Enquiry"
          }
          contextLabel={
            enquiryFlow === "waitlist"
              ? "Waitlist for this property"
              : "Request for stay under 10 nights"
          }
          submitLabel={
            enquiryFlow === "waitlist" ? "Join Waitlist" : "Send Enquiry"
          }
          successTitle={
            enquiryFlow === "waitlist" ? "Added to Waitlist" : "Enquiry Sent"
          }
          successMessage={
            enquiryFlow === "waitlist"
              ? "We'll notify you when this property becomes available."
              : "Our team will contact you for your short-stay request."
          }
          hideMessageField={enquiryFlow === "waitlist"}
        />

        {/* Price Details Dialog */}
        {isPriceDetailsOpen && (
          <div className="fixed inset-0 z-[80] flex items-center justify-center p-4">
            <div
              className="absolute inset-0 bg-black/60 backdrop-blur-sm"
              onClick={() => setIsPriceDetailsOpen(false)}
            ></div>
            <div className="relative bg-background w-full max-w-md rounded-2xl shadow-xl overflow-hidden animate-in zoom-in-95 duration-200">
              {/* Header */}
              <div className="flex items-center justify-between p-4 border-b border-border">
                <h3 className="text-lg font-bold">Price details</h3>
                <button
                  onClick={() => setIsPriceDetailsOpen(false)}
                  className="p-2 hover:bg-secondary rounded-full transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Content */}
              <div className="p-6 max-h-[80vh] overflow-y-auto">
                {/* Monthly Charges */}
                <div className="space-y-1 mb-2">
                  <div className="flex justify-between items-start">
                    <span className="text-muted-foreground">
                      Rent ({nights} nights)
                    </span>
                    <span>
                      {trip.pricing.currency} {rentAmount.toLocaleString()}
                    </span>
                  </div>
                  <div className="flex justify-between items-start">
                    <span className="text-muted-foreground">
                      VAT ({vatRate}%)
                    </span>
                    <span>
                      {trip.pricing.currency} {vatAmount.toLocaleString()}
                    </span>
                  </div>
                  <div className="flex justify-between items-start font-bold text-foreground">
                    <span>Stay charges</span>
                    <span>
                      {trip.pricing.currency} {rentTotal.toLocaleString()}
                    </span>
                  </div>
                </div>

                <div className="h-px bg-border my-4"></div>

                {/* Total Charges section */}
                <div className="space-y-4 mb-6">
                  <div className="flex justify-between items-start">
                    <div>
                      <span className="text-muted-foreground block">
                        Total charges
                      </span>
                      <span className="text-xs text-muted-foreground block mt-0.5">
                        {trip.pricing.currency} {rentTotal.toLocaleString()} for{" "}
                        {nights} night{nights !== 1 ? "s" : ""}
                      </span>
                    </div>
                    <span>
                      {trip.pricing.currency} {rentTotal.toLocaleString()}
                    </span>
                  </div>

                  <div className="flex justify-between items-start">
                    <span className="text-muted-foreground">Booking fee</span>
                    <span>
                      {trip.pricing.currency}{" "}
                      {(trip.pricing.fees?.bookingFee || 0).toLocaleString()}
                    </span>
                  </div>

                  <div className="flex justify-between items-start">
                    <div>
                      <span className="text-muted-foreground block">
                        Exit cleaning & restocking fee
                      </span>
                      <span className="text-xs text-muted-foreground block mt-0.5">
                        Supplies, linens, towels
                      </span>
                    </div>
                    <span>
                      {trip.pricing.currency}{" "}
                      {(
                        trip.pricing.fees?.exitCleaningFee || 0
                      ).toLocaleString()}
                    </span>
                  </div>

                  <div className="flex justify-between items-start">
                    <span className="text-muted-foreground">Tourism fee</span>
                    <span>
                      {trip.pricing.currency}{" "}
                      {(trip.pricing.fees?.tourismFee || 0).toLocaleString()}
                    </span>
                  </div>

                  <div className="flex justify-between items-start">
                    <div>
                      <span className="text-muted-foreground block">
                        Refundable security deposit
                      </span>
                      <span className="text-xs text-muted-foreground block mt-0.5">
                        Refunded if no damage is discovered during inspection
                      </span>
                    </div>
                    <span>
                      {trip.pricing.currency} {securityDeposit.toLocaleString()}
                    </span>
                  </div>
                </div>

                <div className="h-px bg-border my-4"></div>

                {/* Grand Total */}
                <div className="flex justify-between items-baseline mb-2">
                  <span className="text-lg font-bold">Total</span>
                  <span className="text-lg font-bold text-primary">
                    {trip.pricing.currency} {grandTotal.toLocaleString()}
                  </span>
                </div>

                <button
                  onClick={() => setIsPaymentTimelineOpen(true)}
                  className="text-primary font-medium text-sm hover:underline"
                >
                  View payment timeline
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Payment Details Dialog */}
        <Dialog
          open={isPaymentDialogOpen}
          onOpenChange={setIsPaymentDialogOpen}
        >
          <DialogContent className="sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle>Complete Your Booking</DialogTitle>
              <DialogDescription>
                Enter your details to proceed with the payment for your {nights}
                -night stay
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              {/* Booking Summary */}
              <div className="bg-muted/50 rounded-lg p-4 space-y-2">
                <h4 className="font-semibold text-sm">Booking Summary</h4>
                <div className="text-sm space-y-1">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Check-in:</span>
                    <span className="font-medium">
                      {checkInDate ? format(checkInDate, "MMM dd, yyyy") : "-"}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Check-out:</span>
                    <span className="font-medium">
                      {checkOutDate
                        ? format(checkOutDate, "MMM dd, yyyy")
                        : "-"}
                    </span>
                  </div>
                  {/* <div className="flex justify-between">
                    <span className="text-muted-foreground">Guests:</span>
                    <span className="font-medium">{guests}</span>
                  </div> */}
                  <div className="flex justify-between pt-2 border-t">
                    <span className="font-semibold">Total Amount:</span>
                    <span className="font-bold text-primary">
                      {trip.pricing.currency} {grandTotal.toLocaleString()}
                    </span>
                  </div>
                </div>
              </div>

              {/* Customer Information Form */}
              <div className="space-y-3">
                <div>
                  <Label htmlFor="name">Full Name *</Label>
                  <Input
                    id="name"
                    value={customerInfo.name}
                    onChange={(e) =>
                      setCustomerInfo({ ...customerInfo, name: e.target.value })
                    }
                    placeholder="John Doe"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="email">Email Address *</Label>
                  <Input
                    id="email"
                    type="email"
                    value={customerInfo.email}
                    onChange={(e) =>
                      setCustomerInfo({
                        ...customerInfo,
                        email: e.target.value,
                      })
                    }
                    placeholder="john.doe@example.com"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="phone">Phone Number</Label>
                  <div className="mt-1">
                    <PhoneInput
                      id="phone"
                      defaultCountry="AE"
                      value={customerInfo.phone}
                      onChange={(val) =>
                        setCustomerInfo({
                          ...customerInfo,
                          phone: val ? String(val) : "",
                        })
                      }
                      placeholder="Mobile number"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="pay-nationality">Nationality</Label>
                  <div className="mt-1">
                    <NationalitySelect
                      id="pay-nationality"
                      value={customerInfo.nationality}
                      onChange={(v) =>
                        setCustomerInfo({ ...customerInfo, nationality: v })
                      }
                    />
                  </div>
                </div>
              </div>

              {/* Restricted nationality: no property currently available — do not allow payment. */}
              {customerInfo.nationality &&
                isRestrictedNationality(customerInfo.nationality) && (
                  <div className="pt-4">
                    <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
                      <p>
                        Thank you for your interest. We&apos;ve noted your
                        details and our team will reach out to you.
                      </p>
                    </div>
                  </div>
                )}

              {/* Payment options appear once the details are filled in (not for restricted nationalities) */}
              {!paymentMode &&
                customerInfo.name &&
                customerInfo.email &&
                customerInfo.nationality &&
                !isRestrictedNationality(customerInfo.nationality) && (
                  <div className="pt-4 grid grid-cols-2 gap-3">
                    <Button
                      onClick={handleProceedToPayment}
                      className="w-full text-xs font-semibold"
                    >
                      Proceed to Payment
                    </Button>
                    <Button
                      onClick={() => setPaymentMode("offline")}
                      variant="outline"
                      className="w-full text-xs font-semibold border-2"
                    >
                      Already Paid
                    </Button>
                  </div>
                )}

              {/* Card Payment Button */}
              {paymentMode === "card" &&
                !isRestrictedNationality(customerInfo.nationality) && (
                  <div className="pt-4 animate-in fade-in slide-in-from-bottom-2">
                    <PaymentButton
                      propertyId={id}
                      amount={grandTotal}
                      customerName={customerInfo.name}
                      customerEmail={customerInfo.email}
                      customerPhone={customerInfo.phone}
                      checkInDate={checkInDate?.toISOString().split("T")[0]}
                      checkOutDate={checkOutDate?.toISOString().split("T")[0]}
                      description={`Booking for ${trip.title}`}
                      nationality={customerInfo.nationality}
                      onSessionCreated={persistPendingBookingAttempt}
                      className="w-full"
                    >
                      Pay via TotalPay
                    </PaymentButton>

                    <p className="text-xs text-muted-foreground text-center mt-3">
                      You will be redirected to secure checkout
                    </p>
                  </div>
                )}

              {/* Already Paid Flow */}
              {paymentMode === "offline" && (
                <div className="pt-4 space-y-3 animate-in fade-in slide-in-from-bottom-2">
                  <div className="p-4 border-2 border-dashed rounded-xl border-border bg-muted/20">
                    <Label className="mb-2 block text-sm font-semibold">
                      Upload Payment Receipt
                    </Label>
                    <p className="text-xs text-muted-foreground mb-3">
                      Please upload a valid PDF, JPG, or PNG invoice.
                    </p>

                    <div className="flex items-center gap-3">
                      <Label
                        htmlFor="invoice-upload"
                        className="flex-1 cursor-pointer min-w-0"
                      >
                        <div className="flex items-center justify-center gap-2 bg-background border border-border shadow-sm rounded-lg px-4 py-3 hover:bg-secondary/50 transition-colors overflow-hidden">
                          <FileUp className="w-5 h-5 shrink-0 text-muted-foreground" />
                          <span className="text-sm font-medium truncate max-w-[250px] sm:max-w-[300px]">
                            {invoiceFile ? invoiceFile.name : "Choose File..."}
                          </span>
                        </div>
                      </Label>
                      <Input
                        id="invoice-upload"
                        type="file"
                        accept=".pdf, .jpg, .jpeg, .png"
                        className="hidden"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) setInvoiceFile(file);
                        }}
                      />
                    </div>
                  </div>

                  {uploadError && (
                    <p className="text-xs text-red-500 font-medium">
                      {uploadError}
                    </p>
                  )}

                  {uploadSuccess && (
                    <div className="flex items-center gap-2 p-3 bg-green-50 text-green-700 rounded-lg text-sm font-medium">
                      <CheckCircle2 className="w-4 h-4" />
                      Invoice uploaded successfully! Verifying...
                    </div>
                  )}

                  {!uploadSuccess && (
                    <div className="flex gap-3">
                      <Button
                        variant="outline"
                        onClick={() => setPaymentMode(null)}
                        className="w-1/3"
                      >
                        Cancel
                      </Button>
                      <Button
                        onClick={handleAlreadyPaidSubmit}
                        disabled={isUploading || !invoiceFile}
                        className="flex-1"
                      >
                        {isUploading ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Uploading...
                          </>
                        ) : (
                          "Submit Invoice"
                        )}
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>

        {/* Payment Timeline Dialog */}
        {isPaymentTimelineOpen && (
          <div className="fixed inset-0 z-[90] flex items-center justify-center p-4">
            <div
              className="absolute inset-0 bg-black/60 backdrop-blur-sm"
              onClick={() => setIsPaymentTimelineOpen(false)}
            ></div>
            <div className="relative bg-background w-full max-w-md rounded-2xl shadow-xl overflow-hidden animate-in zoom-in-95 duration-200">
              {/* Header */}
              <div className="flex items-center justify-between p-4 border-b border-border">
                <h3 className="text-lg font-bold">Payment timeline</h3>
                <button
                  onClick={() => setIsPaymentTimelineOpen(false)}
                  className="p-2 hover:bg-secondary rounded-full transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Content */}
              <div className="p-6 max-h-[80vh] overflow-y-auto">
                <p className="text-muted-foreground text-sm mb-6">
                  Payments are pro-rated based on your chosen payment plan and
                  dates of stay.
                </p>

                {/* Timeline Item: Due Now */}
                <div className="relative pl-6 pb-6">
                  {/* Timeline Line */}
                  <div className="absolute top-2 left-0 w-1.5 h-1.5 bg-foreground rounded-full z-10"></div>
                  <div className="absolute top-2 left-0.5 w-[2px] h-full bg-border -ml-px border-l-2 border-dotted border-border"></div>

                  <div className="flex justify-between items-baseline mb-3">
                    <span className="font-bold text-foreground">Due now</span>
                    <span className="font-bold text-foreground">
                      {trip.pricing.currency} {grandTotal.toLocaleString()}
                    </span>
                  </div>

                  {/* Gray Box for Breakdown */}
                  <div className="bg-secondary/30 rounded-lg p-4 mb-2">
                    <h4 className="text-sm font-bold text-foreground mb-3">
                      One-time charges for your full stay
                    </h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Rent</span>
                        <span>
                          {trip.pricing.currency} {rentAmount.toLocaleString()}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">VAT</span>
                        <span>
                          {trip.pricing.currency} {vatAmount.toLocaleString()}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">
                          Tourism fee
                        </span>
                        <span>
                          {trip.pricing.currency}{" "}
                          {(
                            trip.pricing.fees?.tourismFee || 0
                          ).toLocaleString()}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">
                          Booking fee
                        </span>
                        <span>
                          {trip.pricing.currency}{" "}
                          {(
                            trip.pricing.fees?.bookingFee || 0
                          ).toLocaleString()}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">
                          Exit cleaning & restocking fee
                        </span>
                        <span>
                          {trip.pricing.currency}{" "}
                          {(
                            trip.pricing.fees?.exitCleaningFee || 0
                          ).toLocaleString()}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">
                          Security deposit
                        </span>
                        <span>
                          {trip.pricing.currency}{" "}
                          {securityDeposit.toLocaleString()}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Timeline Item: Deposit Return */}
                <div className="relative pl-6">
                  {/* Timeline Line - End point */}
                  <div className="absolute top-2 left-0 w-1.5 h-1.5 bg-foreground rounded-full z-10"></div>

                  <div className="mb-1">
                    <span className="text-foreground">
                      Receive your {trip.pricing.currency}{" "}
                      {securityDeposit.toLocaleString()} deposit back
                    </span>
                  </div>
                  <p className="text-muted-foreground text-sm">
                    Usually within 15 calendar days of move-out
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
